/**
 * B2M native tracker for B2M Tracker
 * Acts as a dedicated tracking API similar to Google Analytics
 */
import PlatformDetector from './platforms/index';
import { capturePixelParamsFromUrl } from '../utils/pixel-params.js';

// Injected by webpack DefinePlugin at build time. Guarded so the SDK still runs
// (e.g. in a unit test importing this module directly) when it isn't defined.
const SDK_VERSION = (typeof __SDK_VERSION__ !== 'undefined') ? __SDK_VERSION__ : 'dev';

export default class B2MCustomTracker {
  constructor(tracker) {
    this.tracker = tracker;
    this.trackingQueue = [];
    this.isReady = false;
    this.autoTrackingEnabled = true;
    this.lastScrollDepth = 0;
    this.startTime = Date.now();
    this.pageLoadTime = null;
    this.trackedElements = new Map(); // Changed to Map to store timestamps
    this.sessionStarted = false;
    this.pageViewed = false; // Track if a page has been viewed
    this.itemAddedToCart = false; // Track if an item has been added to cart
    
    // Duplicate prevention settings (will be updated from config)
    this.duplicatePreventionTimeout = 5000; // 5 seconds default
    
    // E-commerce platform detector
    this.platformDetector = null;
    
    // Bot detection result (cached)
    this._isBot = null;
    
    // Event handlers registry
    this.eventHandlers = new Map();
    this.setupEventHandlers();
  }
  
  // ============== BOT DETECTION ==============
  
  /**
   * Detect if the current visitor is a bot/crawler
   * GTM counts bot traffic, we filter it out for cleaner data
   * @returns {boolean} true if bot detected
   */
  isBot() {
    // Check if bot filtering is enabled in config
    if (this.tracker?.config?.filterBots === false) {
      return false;
    }
    
    // Return cached result if already checked
    if (this._isBot !== null) return this._isBot;
    
    const ua = (navigator.userAgent || '').toLowerCase();
    
    // 1. Check for common bot user agents
    const botPatterns = [
      'googlebot', 'bingbot', 'slurp', 'duckduckbot', 'baiduspider',
      'yandexbot', 'sogou', 'exabot', 'facebot', 'facebookexternalhit',
      'ia_archiver', 'alexabot', 'mj12bot', 'ahrefsbot', 'semrushbot',
      'dotbot', 'rogerbot', 'screaming frog', 'uptimerobot', 'pingdom',
      'phantomjs', 'headlesschrome', 'lighthouse', 'chrome-lighthouse',
      'pagespeed', 'gtmetrix', 'google page speed', 'searchatlas',
      'petalbot', 'bytespider', 'applebot', 'twitterbot', 'linkedinbot',
      'whatsapp', 'telegrambot', 'discordbot', 'slackbot',
      'crawler', 'spider', 'scraper', 'bot/', 'bot;',
      'archive.org_bot', 'seznambot', 'mail.ru', 'ccbot',
      'dataforseo', 'blexbot', 'zoominfobot', 'gptbot', 'claudebot'
    ];
    
    const isUABot = botPatterns.some(pattern => ua.includes(pattern));
    if (isUABot) {
      this._isBot = true;
      this.tracker.logger.log('[B2M] Bot detected via user agent:', ua.substring(0, 100));
      return true;
    }
    
    // 2. Check for headless browser indicators
    if (navigator.webdriver === true) {
      this._isBot = true;
      this.tracker.logger.log('[B2M] Bot detected: navigator.webdriver is true');
      return true;
    }
    
    // 3. Check for missing/fake browser features that real browsers have
    // PhantomJS and headless browsers often lack these
    if (!window.chrome && ua.includes('chrome')) {
      // Chrome user agent but no window.chrome object = likely headless
      this._isBot = true;
      this.tracker.logger.log('[B2M] Bot detected: Chrome UA without window.chrome');
      return true;
    }
    
    // 4. Check for automation tools
    if (window._phantom || window.__nightmare || window.callPhantom) {
      this._isBot = true;
      this.tracker.logger.log('[B2M] Bot detected: automation tool detected');
      return true;
    }
    
    // 5. Check for very small or zero-size screen (headless browsers)
    if (screen.width === 0 || screen.height === 0) {
      this._isBot = true;
      this.tracker.logger.log('[B2M] Bot detected: zero screen size');
      return true;
    }
    
    // 6. Check for missing language (bots often don't set this)
    if (!navigator.language && !navigator.languages?.length) {
      this._isBot = true;
      this.tracker.logger.log('[B2M] Bot detected: no language set');
      return true;
    }
    
    this._isBot = false;
    return false;
  }
  
  setupEventHandlers() {
    // E-commerce handlers
    this.eventHandlers.set('ecommerce', {
      view_item: this.setupProductViewTracking.bind(this),
      view_item_list: this.setupProductListTracking.bind(this),
      select_item: this.setupSelectItemTracking.bind(this),
      add_to_cart: this.setupAddToCartTracking.bind(this),
      remove_from_cart: this.setupRemoveFromCartTracking.bind(this),
      view_cart: this.setupViewCartTracking.bind(this),
      begin_checkout: this.setupCheckoutTracking.bind(this),
      add_shipping_info: this.setupShippingInfoTracking.bind(this),
      add_payment_info: this.setupPaymentInfoTracking.bind(this),
      purchase: this.setupPurchaseTracking.bind(this),
      refund: this.setupRefundTracking.bind(this),
      add_to_wishlist: this.setupAddToWishlistTracking.bind(this),
      remove_from_wishlist: this.setupRemoveFromWishlistTracking.bind(this),
      view_promotion: this.setupViewPromotionTracking.bind(this),
      select_promotion: this.setupSelectPromotionTracking.bind(this),
      coupon_apply: this.setupCouponApplyTracking.bind(this)
    });
    
    // Media handlers
    this.eventHandlers.set('media', {
      video_start: this.setupVideoTracking.bind(this),
      video_complete: this.setupVideoTracking.bind(this),
      video_pause: this.setupVideoTracking.bind(this),
      image_zoom: this.setupImageZoomTracking.bind(this)
    });
    
    // Search handlers
    this.eventHandlers.set('search', {
      search: this.setupSearchTracking.bind(this),
      filter_apply: this.setupFilterTracking.bind(this),
      sort_change: this.setupSortTracking.bind(this)
    });
    
    // Communication handlers
    this.eventHandlers.set('communication', {
      phone_click: this.setupPhoneClickTracking.bind(this),
      contact_form: this.setupContactFormTracking.bind(this)
    });
    
    // Marketing handlers
    this.eventHandlers.set('marketing', {
      banner_click: this.setupBannerClickTracking.bind(this),
      newsletter_signup: this.setupNewsletterTracking.bind(this),
      popup_view: this.setupPopupTracking.bind(this)
    });
    
    // Performance handlers
    this.eventHandlers.set('performance', {
      api_timing: this.setupApiTimingTracking.bind(this),
      network_change: this.setupNetworkTracking.bind(this)
    });
    
    // Engagement handlers
    this.eventHandlers.set('engagement', {
      user_idle: this.setupIdleTracking.bind(this),
      tab_change: this.setupTabChangeTracking.bind(this),
      modal_open: this.setupModalTracking.bind(this)
    });
    
    // Mobile handlers
    this.eventHandlers.set('mobile', {
      orientation_change: this.setupOrientationTracking.bind(this),
      app_download_click: this.setupAppDownloadTracking.bind(this)
    });
  }
  
  setup() {
    this.tracker.logger.log('[B2M] B2MCustomTracker setup called - page load detected');

    // Parse UTM/campaign params first — needed so session_start carries attribution data
    this._trackCampaignParams();

    // Send session_start immediately (before page_view) when this is a new session.
    // Must run before sendImmediatePageView() because that calls generateSessionId()
    // which would write a session ID to sessionStorage, making the session look active.
    // NOTE: this only ENQUEUES session_start; the flush happens via the single
    // sendBulkEvents() inside sendImmediatePageView() below, so session_start and
    // page_view ship together in one batch (no duplicate session_start).
    const sessionStartEnabled = this.tracker.config.isEventEnabled('session_start');
    if (sessionStartEnabled) {
      this._sendImmediateSessionStart();
    }

    // IMMEDIATELY send pageview - don't wait for anything else.
    // This bypasses track()/isEventEnabled() (direct queue), so guard it the
    // same way session_start is guarded above — respects abMode.
    if (this.tracker.config.isEventEnabled('page_view')) {
      this.sendImmediatePageView();
    }

    // Fallback flush: if page_view didn't send the batch (page_view disabled, or
    // deferred while the page is prerendering/hidden), make sure a queued
    // session_start still goes out instead of waiting for the next event.
    if (sessionStartEnabled && !this.pageViewed && this.tracker.storage.getBulkQueueSize() > 0) {
      this.tracker.sendBulkEvents();
    }
    
    // Create the global b2m object
    window.b2m = window.b2m || {};
    
    // Setup basic tracking methods
    window.b2m.track = this.track.bind(this);
    window.b2m.pageView = this.pageView.bind(this);
    window.b2m.identify = this.identify.bind(this);
    window.b2m.enableAutoTracking = this.enableAutoTracking.bind(this);
    window.b2m.disableAutoTracking = this.disableAutoTracking.bind(this);
    window.b2m.trackAddToCart = this.trackAddToCart.bind(this);
    
    // User profile methods (Amplitude/Mixpanel parity)
    window.b2m.setUserProperties = this.setUserProperties.bind(this);
    window.b2m.setUserPropertiesOnce = this.setUserPropertiesOnce.bind(this);
    window.b2m.incrementUserProperty = this.incrementUserProperty.bind(this);
    window.b2m.appendUserProperty = this.appendUserProperty.bind(this);
    
    // Revenue tracking (Amplitude parity)
    window.b2m.trackRevenue = this.trackRevenue.bind(this);
    
    // Consent management (GDPR/KVKK)
    window.b2m.giveConsent = this.giveConsent.bind(this);
    window.b2m.revokeConsent = this.revokeConsent.bind(this);
    window.b2m.hasConsent = this.hasConsent.bind(this);
    
    // Event group control methods
    window.b2m.enableEventGroup = this.enableEventGroup.bind(this);
    window.b2m.disableEventGroup = this.disableEventGroup.bind(this);
    window.b2m.getEnabledEvents = this.getEnabledEvents.bind(this);
    
    // Process any events that were queued before initialization
    this.processQueue();
    
    // Setup automatic tracking based on event groups (skip pageview - already sent)
    if (this.autoTrackingEnabled) {
      this.setupAutoTracking(true); // true = skip initial pageview
    }
    
    // Update duplicate prevention timeout from config
    this.duplicatePreventionTimeout = this.tracker.config.duplicatePreventionTimeout || 5000;
    
    // Setup GTM DataLayer listener
    this.setupGTMDataLayerListener();
    
    // Setup SPA navigation tracking
    this.setupSPANavigationTracking();
    
    // Initialize e-commerce platform detection if ecommerce group is enabled
    this.initPlatformDetection();
    
    this.isReady = true;
    this.tracker.logger.log("B2M tracker set up");
    
    // Setup session end tracking (Mixpanel/Amplitude/Countly parity)
    this._setupSessionEndTracking();
  }
  
  // ============== SESSION END TRACKING ==============

  /**
   * Track session_end when user leaves the page.
   *
   * Browser event behaviour matrix:
   * ┌─────────────────────────────┬───────────┬──────────────────┬──────────┐
   * │ Scenario                    │ pagehide  │ visibilitychange │ pageshow │
   * ├─────────────────────────────┼───────────┼──────────────────┼──────────┤
   * │ Tab close / window close    │ ✅ hidden  │ ✅ hidden        │ ❌       │
   * │ Navigate away               │ ✅ hidden  │ ✅ hidden        │ ❌       │
   * │ Switch to another tab       │ ❌         │ ✅ hidden        │ ❌       │
   * │ Switch back to this tab     │ ❌         │ ✅ visible       │ ❌       │
   * │ Back/forward (BFCache miss) │ ✅ hidden  │ ✅ hidden        │ ✅ persisted=false │
   * │ Back/forward (BFCache hit)  │ ✅ hidden  │ ✅ hidden        │ ✅ persisted=true  │
   * └─────────────────────────────┴───────────┴──────────────────┴──────────┘
   *
   * Strategy:
   *  - pagehide           → send session_end (tab close, navigate away, BFCache)
   *  - visibilitychange hidden → send session_end as fallback (mobile skip pagehide)
   *  - visibilitychange visible → reset guard (user came back from another tab)
   *  - pageshow persisted → reset guard + timer (page restored from BFCache)
   *
   * Duplicate guard: _sessionEndSent prevents double-send when both events fire
   * for the same departure (e.g. tab close fires pagehide + visibilitychange).
   * The guard is cleared when the user returns so the NEXT departure is tracked.
   */
  _setupSessionEndTracking() {
    this._pageCount = 1;
    this._eventCount = 0;
    this._sessionEndSent = false;
    this._sessionEndTimer = null;
    this._maxSessionTimer = null;

    const sendSessionEnd = () => {
      if (!this.sessionStarted) return;
      if (this._sessionEndSent) return;
      this._sessionEndSent = true;

      if (this._sessionEndTimer) {
        clearTimeout(this._sessionEndTimer);
        this._sessionEndTimer = null;
      }

      if (this._maxSessionTimer) {
        clearTimeout(this._maxSessionTimer);
        this._maxSessionTimer = null;
      }

      if (!this.tracker.config.isEventEnabled('session_end')) return;

      const sessionDuration = Math.round((Date.now() - this.startTime) / 1000);

      const event = {
        event: 'session_end',
        session_id: this.tracker.storage.getSessionId() || undefined,
        session_duration_seconds: sessionDuration,
        page_count: this._pageCount || 1,
        event_count: this._eventCount || 0,
        page_path: window.location.pathname,
        page_title: document.title,
        is_bot: this.isBot(),
        interceptor: 'b2m',
        event_group: 'core',
        sdk_version: SDK_VERSION
      };

      this.tracker.storage.addToBulkQueue(event);
      this.tracker.sendBulkEvents(true); // sendBeacon — survives page unload
    };

    // ── 1. pagehide ──────────────────────────────────────────────────────────
    // Fires on: tab close, navigate away, back/forward (BFCache or not)
    // Real departure — send session_end immediately.
    window.addEventListener('pagehide', sendSessionEnd);

    // ── 2. visibilitychange ──────────────────────────────────────────────────
    // hidden → start inactivity timer instead of sending immediately.
    //          If the user returns within sessionTimeout, session continues.
    //          If the timer expires (user never came back), send session_end.
    // visible → cancel the timer, session resumes normally.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        if (this._sessionEndSent) return;
        const timeout = this.tracker.config.sessionTimeout || 30 * 60 * 1000;
        this._sessionEndTimer = setTimeout(() => {
          this._sessionEndTimer = null;
          sendSessionEnd();
        }, timeout);
      } else {
        // User returned before timeout — cancel and keep session alive
        if (this._sessionEndTimer) {
          clearTimeout(this._sessionEndTimer);
          this._sessionEndTimer = null;
        }
        this._sessionEndSent = false;
      }
    });

    // ── 3. pageshow (BFCache restore) ────────────────────────────────────────
    // Page restored from BFCache — cancel any pending timer and reset.
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) {
        if (this._sessionEndTimer) {
          clearTimeout(this._sessionEndTimer);
          this._sessionEndTimer = null;
        }
        this._sessionEndSent = false;
        this.startTime = Date.now();
        this.tracker.logger.log('[B2M] Page restored from BFCache — session timer reset');
      }
    });

    // ── 4. Max session duration (24 hr hard cap) ──────────────────────────────
    // Even if the user stays active, end the session after maxSessionDuration.
    // Remaining time is calculated from the stored session start so the cap
    // survives SPA navigations within the same tab.
    const maxDuration = this.tracker.config.maxSessionDuration || 24 * 60 * 60 * 1000;
    const sessionStartTime = this.tracker.storage.getSessionStartTime() || Date.now();
    const elapsed = Date.now() - sessionStartTime;
    const remaining = maxDuration - elapsed;

    if (remaining > 0) {
      this._maxSessionTimer = setTimeout(() => {
        this._maxSessionTimer = null;
        this.tracker.logger.log('[B2M] Max session duration reached — ending session');
        sendSessionEnd();
      }, remaining);
    } else {
      // Session already exceeded max — end immediately
      sendSessionEnd();
    }
  }
  
  // ============== EVENT VALIDATION & SANITIZATION ==============
  
  /**
   * Validate and sanitize event data before sending
   * Mixpanel limits: 255 props, 255-char keys, 255-char string values (warning only)
   * Amplitude limits: 1024 props, 1024-char keys
   * We adopt sensible defaults and log warnings
   */
  _sanitizeEventData(eventName, data) {
    if (!data || typeof data !== 'object') return {};
    
    const MAX_PROPERTIES = 500;
    const MAX_KEY_LENGTH = 256;
    const MAX_STRING_VALUE_LENGTH = 8192;
    
    const sanitized = {};
    let propCount = 0;
    
    for (const [key, value] of Object.entries(data)) {
      // Limit property count
      if (propCount >= MAX_PROPERTIES) {
        this.tracker.logger.warn(`[B2M] Event '${eventName}' has more than ${MAX_PROPERTIES} properties, truncating`);
        break;
      }
      
      // Truncate long keys
      let safeKey = key;
      if (key.length > MAX_KEY_LENGTH) {
        safeKey = key.substring(0, MAX_KEY_LENGTH);
        this.tracker.logger.warn(`[B2M] Property key truncated: ${key}`);
      }
      
      // Skip undefined values (but allow null)
      if (value === undefined) continue;
      
      // Truncate very long string values
      if (typeof value === 'string' && value.length > MAX_STRING_VALUE_LENGTH) {
        sanitized[safeKey] = value.substring(0, MAX_STRING_VALUE_LENGTH);
        this.tracker.logger.warn(`[B2M] Property '${safeKey}' value truncated (${value.length} chars)`);
      } else {
        sanitized[safeKey] = value;
      }
      
      propCount++;
    }
    
    return sanitized;
  }
  
  // ============== UTM / CAMPAIGN TRACKING ==============
  
  /**
   * Parse and track UTM/campaign parameters from URL
   * Mixpanel, Amplitude, GA4 all do this automatically
   */
  _trackCampaignParams() {
    try {
      const params = new URLSearchParams(window.location.search);
      const campaignParams = {};
      let hasCampaign = false;

      const utmKeys = [
        'utm_source', 'utm_medium', 'utm_campaign',
        'utm_content', 'utm_term', 'utm_id'
      ];

      const additionalKeys = [
        'gclid', 'fbclid', 'msclkid', 'dclid', 'twclid',
        'li_fat_id', 'mc_cid', 'mc_eid', 'ref', 'source',
        // Privacy-era Google Ads click ids (iOS/consent) + extra paid social ids.
        // Needed so paid traffic is still detected when gclid is absent.
        'wbraid', 'gbraid', 'gad_source', 'ttclid', 'sccid', 'epik'
      ];

      utmKeys.forEach(key => {
        const value = params.get(key);
        if (value) { campaignParams[key] = value; hasCampaign = true; }
      });

      additionalKeys.forEach(key => {
        const value = params.get(key);
        if (value) { campaignParams[key] = value; hasCampaign = true; }
      });

      if (hasCampaign) {
        // Last-touch: overwrite on every visit that has UTM params
        this.tracker.storage.setItem('b2m_campaign_params', campaignParams);
        this.tracker.logger.log('[B2M] Campaign params detected:', campaignParams);
      }

      // First-touch UTM — captured on the first visit that actually HAS utm.
      // If an earlier visit landed without utm, the stored value is empty ({});
      // upgrade it when real utm finally arrives. Once it holds real values it is
      // never overwritten (true first-touch).
      const existingInitialUtm = this.tracker.storage.getItem('b2m_initial_utm');
      const initialUtmEmpty = !existingInitialUtm || Object.keys(existingInitialUtm).length === 0;
      if (initialUtmEmpty) {
        const initialUtm = {};
        utmKeys.forEach(key => {
          if (campaignParams[key]) initialUtm[`initial_${key}`] = campaignParams[key];
        });
        // Only persist when we have real utm (don't lock an empty {} that would
        // block a later real first-touch); seed {} only if nothing exists yet.
        if (Object.keys(initialUtm).length > 0) {
          this.tracker.storage.setItem('b2m_initial_utm', initialUtm);
          this.tracker.logger.log('[B2M] First-touch UTM captured:', initialUtm);
        } else if (!existingInitialUtm) {
          this.tracker.storage.setItem('b2m_initial_utm', {});
        }
      }

      // Referrer — current touch
      // Always store referrer info; use '$direct' when there is no external referrer
      // so that breakdown by referrer_domain always has a bucket for every event.
      let currentReferrer = { referring_url: '', referrer_domain: '$direct' };
      if (document.referrer) {
        try {
          const referrerUrl = new URL(document.referrer);
          if (!this._isInternalReferrer(referrerUrl.hostname)) {
            currentReferrer = {
              referring_url: document.referrer,
              referrer_domain: referrerUrl.hostname
            };
          }
        } catch (e) { /* invalid referrer URL */ }
      }
      this.tracker.storage.setItem('b2m_referrer', currentReferrer);

      // First-touch referrer — same "first real touch" rule: if the stored
      // first-touch is still $direct (landed directly first), upgrade it when a
      // real external referrer later appears; a real domain is never overwritten.
      const existingInitialReferrer = this.tracker.storage.getItem('b2m_initial_referrer');
      const initialReferrerDirect = !existingInitialReferrer ||
        !existingInitialReferrer.initial_referring_domain ||
        existingInitialReferrer.initial_referring_domain === '$direct';
      if (initialReferrerDirect) {
        let initialReferrer = { initial_referring_domain: '$direct', initial_referring_url: '' };
        if (document.referrer) {
          try {
            const referrerUrl = new URL(document.referrer);
            if (!this._isInternalReferrer(referrerUrl.hostname)) {
              initialReferrer = {
                initial_referring_domain: referrerUrl.hostname,
                initial_referring_url: document.referrer
              };
            }
          } catch (e) { /* ignore */ }
        }
        // Write when seeding for the first time, or upgrading $direct → a real domain.
        if (!existingInitialReferrer || initialReferrer.initial_referring_domain !== '$direct') {
          this.tracker.storage.setItem('b2m_initial_referrer', initialReferrer);
          this.tracker.logger.log('[B2M] First-touch referrer captured:', initialReferrer);
        }
      }

      // Stamp the acquisition time on a fresh campaign or external referral.
      // getAttributionUserParams() stops sending UTM/referrer as user props once
      // this is >32 days old. Direct visits don't refresh it, so the window
      // counts from the last real acquisition (last-touch).
      if (hasCampaign || currentReferrer.referrer_domain !== '$direct') {
        this.tracker.storage.setItem('b2m_attribution_ts', Date.now());
      }

      // Capture & persist marketing pixel identifiers (gclid, fbclid→_fbc)
      // so subsequent events in this session carry them even after URL changes.
      capturePixelParamsFromUrl(this.tracker.storage);
    } catch (e) {
      this.tracker.logger.error('[B2M] Error parsing campaign params:', e);
    }
  }

  /**
   * Derive a single last-touch traffic source that every event can carry, so any
   * event breaks down by origin without coalescing utm_source/referrer/direct.
   * Priority: tagged campaign (utm_source) → external referrer domain → 'direct'.
   * Does NOT overwrite utm_source — utm_source stays the pure campaign tag, while
   * organic/referral traffic (which has no utm) is still attributed to its domain
   * instead of being mislabeled 'direct'.
   * @param {Object} campaign  b2m_campaign_params
   * @param {Object} referrer  b2m_referrer
   * @returns {string}
   */
  _deriveTrafficSource(campaign, referrer) {
    const c = campaign || {};
    if (c.utm_source) return c.utm_source;
    const dom = referrer && referrer.referrer_domain;
    if (dom && dom !== '$direct') return dom;
    // Click id with no utm/referrer (common on iOS, in-app browsers) → name the
    // originating platform so source isn't misreported 'direct' while the channel
    // says paid/social.
    if (c.gclid || c.wbraid || c.gbraid || c.gad_source || c.dclid) return 'google';
    if (c.msclkid) return 'bing';
    if (c.fbclid) return 'facebook';
    if (c.ttclid) return 'tiktok';
    if (c.twclid) return 'twitter';
    if (c.li_fat_id) return 'linkedin';
    if (c.sccid) return 'snapchat';
    if (c.epik) return 'pinterest';
    return 'direct';
  }

  /**
   * GA4-style channel grouping so every event buckets into paid / organic /
   * social / email / referral / direct without backend joins. Last-touch.
   * Priority: paid → email → organic → social → referral → direct.
   * @param {Object} campaign  b2m_campaign_params
   * @param {Object} referrer  b2m_referrer
   * @returns {string}
   */
  _deriveTrafficChannel(campaign, referrer) {
    const c = campaign || {};
    const medium = String(c.utm_medium || '').toLowerCase();
    const refDom = (referrer && referrer.referrer_domain) || '';
    const hasExternalRef = refDom && refDom !== '$direct';

    // Ad-specific click ids → unambiguously paid: gclid/wbraid/gbraid (Google Ads),
    // msclkid (Microsoft Ads), dclid (Display). fbclid/ttclid are appended to ALL
    // platform link clicks (not only ads), so they don't imply paid on their own.
    const hasPaidClickId = ['gclid', 'wbraid', 'gbraid', 'msclkid', 'dclid'].some(k => c[k]);
    // Paid mediums: cpc, ppc, paid*, paid_social, display, cpm, banner, retargeting…
    if (hasPaidClickId || /^(cpc|ppc|paid.*|cpm|cpv|cpa|display|banner|retargeting)$/.test(medium)) {
      return 'paid';
    }

    if (/^(e-?mail|newsletter)$/.test(medium)) return 'email';

    // Organic: explicit organic medium, or an untagged visit from a search engine.
    if (medium === 'organic') return 'organic';
    if (!c.utm_source && hasExternalRef && this._isSearchEngine(refDom)) return 'organic';

    // Social: explicit social medium, a social-network referrer, or a social click
    // id (fbclid/ttclid/… are appended to organic social link clicks — not paid,
    // so they land here rather than in 'paid' above).
    if (/^(social|social[-_].*|sm)$/.test(medium)) return 'social';
    if (!c.utm_source && hasExternalRef && this._isSocialDomain(refDom)) return 'social';
    if (['fbclid', 'ttclid', 'sccid', 'epik', 'igshid'].some(k => c[k])) return 'social';

    // Any other tagged campaign or external referrer → referral; nothing → direct.
    if (c.utm_source || hasExternalRef) return 'referral';
    return 'direct';
  }

  /** Known search-engine referrer host → organic traffic. */
  _isSearchEngine(domain) {
    return /(^|\.)(google\.|bing\.|yahoo\.|yandex\.|duckduckgo\.|baidu\.|ecosia\.|ask\.com|aol\.|naver\.|seznam\.|startpage\.|qwant\.|brave\.)/.test(String(domain).toLowerCase());
  }

  /** Known social-network referrer host → social traffic. */
  _isSocialDomain(domain) {
    return /(^|\.)(facebook\.|fb\.com|fb\.me|instagram\.|t\.co|twitter\.|x\.com|linkedin\.|lnkd\.in|pinterest\.|reddit\.|tiktok\.|youtube\.|youtu\.be|snapchat\.|whatsapp\.|telegram\.|t\.me|quora\.|tumblr\.|vk\.com|medium\.com)/.test(String(domain).toLowerCase());
  }

  /**
   * Send session_start IMMEDIATELY when this is a new session.
   * Mirrors sendImmediatePageView — bypasses track() so it works before isReady.
   * Must be called BEFORE sendImmediatePageView so that generateSessionId() hasn't
   * yet written a session ID to sessionStorage (which would make the session look active).
   */
  _sendImmediateSessionStart() {
    const existingSessionId = this.tracker.storage.getSessionId();
    const sessionActive = existingSessionId
      && !this.tracker.storage.isSessionExpired(this.tracker.config.sessionTimeout);

    if (sessionActive) {
      this.tracker.logger.log('[B2M] Active session found, skipping session_start');
      this.sessionStarted = true;
      return;
    }

    // No active session — create one now and fire session_start immediately
    const newSessionId = this.tracker.bulkApi.generateSessionId();
    this.tracker.logger.log('[B2M] New session, sending session_start:', newSessionId);

    const deviceProps = this.tracker.browserInfo.getAutoProperties();
    const campaignParams = this.tracker.storage.getItem('b2m_campaign_params') || {};
    const referrerInfo  = this.tracker.storage.getItem('b2m_referrer') || {};
    const initialUtm    = this.tracker.storage.getItem('b2m_initial_utm') || {};
    const initialReferrer = this.tracker.storage.getItem('b2m_initial_referrer') || {};

    const event = {
      event: 'session_start',
      session_id: newSessionId,
      session_start_time: new Date().toISOString(),
      page_path: window.location.pathname,
      page_title: document.title,
      // No raw `source` field: Mixpanel/Amplitude don't emit one. Traffic origin
      // already travels in standard fields below — utm_source (campaign source),
      // referrer_domain (clean domain) and referring_url (full URL).
      ...deviceProps,
      ...campaignParams,
      ...referrerInfo,
      ...initialUtm,
      ...initialReferrer,
      traffic_source: this._deriveTrafficSource(campaignParams, referrerInfo),
      traffic_channel: this._deriveTrafficChannel(campaignParams, referrerInfo),
      interceptor: 'b2m',
      event_group: 'core',
      sdk_version: SDK_VERSION,
      is_bot: this.isBot()
    };

    // Enqueue only — do NOT flush here. The single sendBulkEvents() inside
    // sendImmediatePageView() sends session_start + page_view together in one
    // batch. Flushing here too would re-send session_start in the page_view
    // batch, because the queue isn't cleared until the HTTP response resolves.
    this.tracker.storage.addToBulkQueue(event);
    this.sessionStarted = true;
  }

  /**
   * Send pageview IMMEDIATELY when SDK loads
   * This ensures we capture the pageview even if user leaves quickly
   * Optimized: Bot filtering, BFCache support, prerender detection
   */
  sendImmediatePageView() {
    // Handle prerendered pages - wait until page becomes visible
    if (document.prerendering || document.visibilityState === 'hidden') {
      this.tracker.logger.log('[B2M] Page is prerendering or hidden, deferring pageview');
      const handleVisibility = () => {
        if (document.visibilityState === 'visible') {
          document.removeEventListener('visibilitychange', handleVisibility);
          // Remove prerendering listener if it exists
          document.removeEventListener('prerenderingchange', handleVisibility);
          this._doSendPageView('prerender_activation');
        }
      };
      document.addEventListener('visibilitychange', handleVisibility);
      // Modern Speculation Rules API
      if (document.prerendering) {
        document.addEventListener('prerenderingchange', handleVisibility);
      }
      return;
    }
    
    // Detect navigation type
    let navigationType = 'navigate';
    
    if (window.performance && window.performance.getEntriesByType) {
      const navEntries = window.performance.getEntriesByType('navigation');
      if (navEntries.length > 0) {
        navigationType = navEntries[0].type;
      }
    } else if (window.performance && window.performance.navigation) {
      const navType = window.performance.navigation.type;
      if (navType === 1) navigationType = 'reload';
      else if (navType === 2) navigationType = 'back_forward';
    }
    
    this._doSendPageView(navigationType);

    // BFCache support: fire page_view when a page is restored from the cache.
    // Skip when SPA tracking is enabled — the popstate listener already fires
    // a page_view for back/forward navigation, so firing here too would duplicate.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted && !this.tracker.config.enableSPATracking) {
        this.tracker.logger.log('[B2M] Page restored from BFCache, sending pageview');
        this._doSendPageView('back_forward_cache');
      }
    });
  }
  
  /**
   * Internal method to actually send the pageview event
   * @param {string} navigationType - Type of navigation
   */
  _doSendPageView(navigationType) {
    // Detect if running inside an iframe
    const isInIframe = this._isInIframe();
    
    this.tracker.logger.log(`[B2M] Sending IMMEDIATE pageview - navigation type: ${navigationType}${isInIframe ? ' (iframe mode)' : ''}`);
    
    // Create pageview event directly (bypass queue for speed)
    // referrer_domain computed inline as a fallback; the b2m_referrer spread below
    // (already populated because _trackCampaignParams() runs before this in setup())
    // carries the same value plus referring_url.
    let _refDomain = '$direct';
    if (document.referrer) {
      try {
        const _refUrl = new URL(document.referrer);
        if (!this._isInternalReferrer(_refUrl.hostname)) {
          _refDomain = _refUrl.hostname;
        }
      } catch (e) { /* invalid referrer URL */ }
    }

    // Attribution — mirror session_start / track() so the landing page_view also
    // carries utm_source etc. Data is already in storage at this point.
    const campaignParams  = this.tracker.storage.getItem('b2m_campaign_params') || {};
    const referrerInfo    = this.tracker.storage.getItem('b2m_referrer') || {};
    const initialUtm      = this.tracker.storage.getItem('b2m_initial_utm') || {};
    const initialReferrer = this.tracker.storage.getItem('b2m_initial_referrer') || {};

    const event = {
      event: 'page_view',
      page_path: window.location.pathname,
      page_title: document.title || window.location.pathname,
      page_location: window.location.href,
      page_domain: window.location.hostname,
      page_referrer: document.referrer || '',
      referrer_domain: _refDomain,
      navigation_type: navigationType,
      is_initial_load: true,
      is_bot: this.isBot(),  // Tag bot traffic – backend can filter/count
      interceptor: 'b2m',
      event_group: 'core',
      sdk_version: SDK_VERSION,
      ...campaignParams,    // Last-touch UTM (utm_source, utm_medium, …)
      ...referrerInfo,      // Current referrer (referrer_domain, referring_url)
      ...initialUtm,        // First-touch UTM (initial_utm_source, …)
      ...initialReferrer,   // First-touch referrer (initial_referring_domain, …)
      traffic_source: this._deriveTrafficSource(campaignParams, referrerInfo),
      traffic_channel: this._deriveTrafficChannel(campaignParams, referrerInfo),

      // iframe context info
      is_iframe: isInIframe
    };
    
    // Add iframe-specific context
    if (isInIframe) {
      event.iframe_src = window.location.href;
      event.host_page_url = this._getHostPageUrl();
      event.host_page_domain = this._getHostPageDomain();
    }
    
    // Add to queue
    this.tracker.storage.addToBulkQueue(event);
    
    // Send immediately - don't wait for batch
    this.tracker.sendBulkEvents();
    
    this.pageViewed = true;
  }
  
  /**
   * Check if SDK is running inside an iframe
   * @returns {boolean}
   */
  _isInIframe() {
    try {
      return window.self !== window.top;
    } catch (e) {
      // Cross-origin: SecurityError means we are in an iframe
      return true;
    }
  }
  
  /**
   * Get the host (parent) page URL
   * In cross-origin iframes, we can't access parent.location directly
   * but document.referrer gives us the embedding page URL
   * @returns {string}
   */
  _getHostPageUrl() {
    // First try direct parent access (same-origin only)
    try {
      if (window.parent && window.parent !== window && window.parent.location) {
        return window.parent.location.href;
      }
    } catch (e) {
      // Cross-origin: SecurityError - expected behavior
    }
    
    // Fallback: document.referrer contains the embedding page URL
    return document.referrer || 'unknown';
  }
  
  /**
   * Referral exclusion: is this referrer hostname "our own site"?
   * True for the exact current host, and — when cross-subdomain sharing is on
   * (cookieDomain set) — for any host under that parent domain. This stops an
   * internal subdomain hop (birfatura.com → app.birfatura.com) from overwriting
   * the real acquisition referrer with a self-referral. First-touch referrer is
   * preserved because it is shared across subdomains via the parent-domain cookie.
   * @param {string} hostname
   * @returns {boolean}
   */
  _isInternalReferrer(hostname) {
    if (!hostname) return false;
    if (hostname === window.location.hostname) return true;
    // Use the RESOLVED domain from storage (config may hold the literal 'auto').
    const cookieDomain = this.tracker.storage.cookieDomain;
    if (cookieDomain) {
      const base = cookieDomain.replace(/^\./, '');
      if (hostname === base || hostname.endsWith('.' + base)) return true;
    }
    return false;
  }

  /**
   * Extract domain from host page URL
   * @returns {string}
   */
  _getHostPageDomain() {
    const hostUrl = this._getHostPageUrl();
    if (!hostUrl || hostUrl === 'unknown') return 'unknown';
    
    try {
      const url = new URL(hostUrl);
      return url.hostname;
    } catch (e) {
      return 'unknown';
    }
  }
  
  /**
   * Initialize e-commerce platform detection and tracking
   */
  initPlatformDetection() {
    const customEventLib = this.tracker.config.customEventLib || null;
    // Check if ecommerce group is enabled or platform detection is explicitly enabled
    const ecommerceEnabled = this.tracker.config.eventGroups?.ecommerce?.enabled;
    const platformDetectionEnabled = this.tracker.config.enablePlatformDetection !== false;

    // customEventLib (e.g. "pulpo") bypasses the ecommerce-group requirement —
    // the Pulpo VTO tracker registers its own lightweight events, not e-commerce ones.
    const shouldInit = customEventLib || (ecommerceEnabled && platformDetectionEnabled);

    if (shouldInit) {
      this.tracker.logger.log('[B2M] Initializing platform detection...' + (customEventLib ? ` (custom-event-lib: ${customEventLib})` : ''));
      
      try {
        this.platformDetector = new PlatformDetector(this);
        this.platformDetector.init(customEventLib);
        
        // Expose platform info on global b2m object
        window.b2m.getPlatformInfo = () => this.platformDetector?.getPlatformInfo();
        window.b2m.setPlatform = (platform) => this.platformDetector?.setPlatform(platform);
        
        this.tracker.logger.log('[B2M] Platform detection initialized');
      } catch (error) {
        console.error('[B2M] Failed to initialize platform detection:', error);
      }
    } else {
      this.tracker.logger.log('[B2M] Platform detection skipped (ecommerce group disabled or detection disabled)');
    }
  }
  
  /**
   * Setup automatic event tracking based on enabled event groups
   * @param {boolean} skipPageView - Skip initial pageview (already sent)
   */
  setupAutoTracking(skipPageView = false) {
    const autoTrackEvents = this.tracker.config.getAutoTrackEvents();
    
    // Core tracking - skip pageview if already sent
    if (autoTrackEvents.includes('page_view') && !skipPageView) {
      this.trackPageView();
    }
    if (autoTrackEvents.includes('button_click') || autoTrackEvents.includes('link_click')) {
      this.trackClicks();
    }
    if (autoTrackEvents.includes('form_submit')) {
      this.trackFormSubmissions();
    }
    
    // Performance tracking
    if (autoTrackEvents.includes('page_performance')) {
      this.trackPageLoad();
    }
    if (autoTrackEvents.includes('javascript_error')) {
      this.trackErrors();
    }
    
    // Engagement tracking
    if (autoTrackEvents.includes('scroll_depth')) {
      this.trackScrollDepth();
    }
    if (autoTrackEvents.includes('time_on_page')) {
      this.trackEngagementTime();
    }
    if (autoTrackEvents.includes('user_idle')) {
      this.setupIdleTracking();
    }
    
    // Setup auto-tracking for enabled event groups
    Object.entries(this.tracker.config.eventGroups).forEach(([groupName, group]) => {
      if (group.enabled && group.autoTrack) {
        this.setupGroupAutoTracking(groupName, group.autoTrack);
      }
    });
  }
  
  /**
   * Setup auto-tracking for specific event group
   */
  setupGroupAutoTracking(groupName, autoTrackEvents) {
    const handlers = this.eventHandlers.get(groupName);
    if (!handlers) return;
    
    autoTrackEvents.forEach(eventName => {
      if (handlers[eventName]) {
        try {
          handlers[eventName]();
        } catch (error) {
          this.tracker.logger.error(`Failed to setup auto-tracking for ${eventName}:`, error);
        }
      }
    });
  }
  
  /**
   * Track initial page view automatically
   * This runs on every page load including refresh
   */
  trackPageView() {
    // Detect navigation type (reload, navigate, back_forward, etc.)
    let navigationType = 'navigate';
    
    if (window.performance && window.performance.navigation) {
      // Legacy API
      const navType = window.performance.navigation.type;
      if (navType === 1) navigationType = 'reload';
      else if (navType === 2) navigationType = 'back_forward';
    } else if (window.performance && window.performance.getEntriesByType) {
      // Modern Navigation Timing API
      const navEntries = window.performance.getEntriesByType('navigation');
      if (navEntries.length > 0) {
        navigationType = navEntries[0].type; // 'navigate', 'reload', 'back_forward', 'prerender'
      }
    }
    
    this.tracker.logger.log(`[B2M] Initial pageview - navigation type: ${navigationType}`);
    
    // Always send pageview on page load (including refresh)
    this.pageView(window.location.pathname, {
      navigation_type: navigationType,
      is_initial_load: true
    });
  }
  
  /**
   * Track page load performance
   */
  trackPageLoad() {
    if (window.performance && window.performance.timing) {
      window.addEventListener('load', () => {
        setTimeout(() => {
          const timing = window.performance.timing;
          const loadTime = timing.loadEventEnd - timing.navigationStart;
          const dnsTime = timing.domainLookupEnd - timing.domainLookupStart;
          const connectTime = timing.connectEnd - timing.connectStart;
          const requestTime = timing.responseEnd - timing.requestStart;
          const renderTime = timing.domComplete - timing.domLoading;
          
          this.pageLoadTime = loadTime;
          
          this.track('page_performance', {
            load_time_ms: loadTime,
            dns_time_ms: dnsTime,
            connect_time_ms: connectTime,
            request_time_ms: requestTime,
            render_time_ms: renderTime,
            page_url: window.location.href
          });
        }, 0);
      });
    }
  }
  
  /**
   * Track clicks on buttons, links and other interactive elements
   */
  trackClicks() {
    document.addEventListener('click', (event) => {
      if (!this.autoTrackingEnabled) return;
      
      const target = event.target.closest('a, button, [role="button"], input[type="button"], input[type="submit"]');
      if (!target) return;
      
      // Don't track elements we've already tracked recently
      const elementId = this.getElementIdentifier(target);
      const now = Date.now();
      const lastTracked = this.trackedElements.get(elementId);
      
      if (lastTracked && (now - lastTracked) < this.duplicatePreventionTimeout) {
        if (this.tracker.config.debug) {
          this.tracker.logger.log(`Duplicate click prevented for element: ${elementId}, last tracked: ${now - lastTracked}ms ago`);
        }
        return;
      }
      
      let eventData = {
        element_type: target.tagName.toLowerCase(),
        element_id: target.id || '',
        element_class: target.className || '',
        element_text: this.getElementText(target),
        page_path: window.location.pathname,
        page_title: document.title
      };
      
      if (target.tagName === 'A') {
        eventData.href = target.href || '';
        eventData.link_domain = this.getLinkDomain(target.href);
        eventData.link_id = target.id || '';
        eventData.link_classes = target.className || '';
        eventData.link_text = this.getElementText(target);
        
        // Mark this as an outbound link if the domain is different
        if (this.isOutboundLink(target.href)) {
          eventData.is_outbound = true;
          this.track('outbound_click', eventData);
        } else {
          this.track('link_click', eventData);
        }
      } else if (target.tagName === 'BUTTON' || target.getAttribute('role') === 'button' || 
                 target.tagName === 'INPUT' && (target.type === 'button' || target.type === 'submit')) {
        // Add button-specific data
        eventData.button_id = target.id || '';
        eventData.button_name = target.name || '';
        eventData.button_value = target.value || '';
        
        this.track('button_click', eventData);
      }
      
      // Record this element as tracked with current timestamp
      this.trackedElements.set(elementId, now);
      
      // Clean up old tracked elements periodically
      this.cleanupTrackedElements();
    });
  }
  
  /**
   * Clean up old tracked elements to prevent memory leaks
   */
  cleanupTrackedElements() {
    const now = Date.now();
    for (const [elementId, timestamp] of this.trackedElements.entries()) {
      if (now - timestamp > this.duplicatePreventionTimeout) {
        this.trackedElements.delete(elementId);
      }
    }
  }

  /**
   * Track scroll depth
   */
  trackScrollDepth() {
    // Throttle the scroll event
    let scrollTimeout;
    
    window.addEventListener('scroll', () => {
      if (!this.autoTrackingEnabled) return;
      
      if (scrollTimeout) clearTimeout(scrollTimeout);
      
      scrollTimeout = setTimeout(() => {
        const scrollDepth = this.getScrollDepth();
        // Only track if scroll depth has increased by 25% or more
        if (scrollDepth >= this.lastScrollDepth + 25 || (scrollDepth === 100 && this.lastScrollDepth < 100)) {
          this.lastScrollDepth = Math.floor(scrollDepth / 25) * 25; // Round to nearest 25%
          
          this.track('scroll_depth', {
            scroll_depth_percentage: this.lastScrollDepth,
            page_path: window.location.pathname,
            page_title: document.title
          });
        }
      }, 500);
    }, { passive: true });
  }
  
  /**
   * Track form submissions
   */
  trackFormSubmissions() {
    document.addEventListener('submit', (event) => {
      if (!this.autoTrackingEnabled) return;
      
      const form = event.target;
      
      // Don't track password or credit card forms for privacy reasons
      if (this.isSecureForm(form)) return;
      
      const formData = {
        form_id: form.id || '',
        form_name: form.name || '',
        form_classes: form.className || '',
        form_action: form.action || '',
        form_method: form.method || '',
        form_elements: form.elements.length,
        form_fields: this.getFormFieldNames(form),
        page_path: window.location.pathname
      };
      
      this.track('form_submit', formData);
    });
  }
  
  /**
   * Track outbound links
   */
  trackOutboundLinks() {
    // This is handled in the click tracking function
  }
  
  /**
   * Track file downloads
   */
  trackFileDownloads() {
    document.addEventListener('click', (event) => {
      if (!this.autoTrackingEnabled) return;
      
      const link = event.target.closest('a');
      if (!link || !link.href) return;
      
      const fileExtensions = [
        'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'zip', 'rar', 'tar', 
        'gz', '7z', 'exe', 'dmg', 'csv', 'txt', 'json', 'xml', 'jpg', 'jpeg', 
        'png', 'gif', 'mp3', 'mp4', 'wav', 'avi', 'mov', 'mpg', 'mpeg'
      ];
      
      const url = link.href.toLowerCase();
      const extension = url.split('.').pop();
      
      if (fileExtensions.includes(extension)) {
        this.track('file_download', {
          file_extension: extension,
          file_name: url.split('/').pop(),
          file_url: url,
          link_id: link.id || '',
          link_text: this.getElementText(link),
          link_classes: link.className || '',
          page_path: window.location.pathname
        });
      }
    });
  }
  
  /**
   * Track JavaScript errors
   */
  trackErrors() {
    let lastErrorTime = 0;
    const ERROR_THROTTLE = 1000; // 1 second throttle
    
    window.addEventListener('error', (event) => {
      if (!this.autoTrackingEnabled) return;
      
      // Throttle error events to prevent spam
      const now = Date.now();
      if (now - lastErrorTime < ERROR_THROTTLE) {
        return;
      }
      lastErrorTime = now;
      
      // Don't track errors from our own tracker to prevent infinite loops
      if (event.filename && event.filename.includes('b2m-tracker')) {
        return;
      }
      
      this.track('javascript_error', {
        error_message: event.message || '',
        error_source: event.filename || '',
        error_line: event.lineno || '',
        error_column: event.colno || '',
        error_stack: event.error && event.error.stack ? event.error.stack : '',
        page_path: window.location.pathname,
        page_title: document.title
      });
    });
  }
  
  /**
   * Track site searches
   */
  trackSiteSearches() {
    // Check for search parameters in the URL
    const url = new URL(window.location.href);
    const searchParams = ['q', 'query', 'search', 's', 'term', 'keyword'];
    
    searchParams.forEach(param => {
      const searchTerm = url.searchParams.get(param);
      if (searchTerm) {
        this.track('site_search', {
          search_term: searchTerm,
          search_parameter: param,
          page_path: window.location.pathname,
          page_url: window.location.href
        });
      }
    });
  }
  
  /**
   * Track engagement time when user leaves
   */
  trackEngagementTime() {
    let engagementStartTime = Date.now();
    let totalEngagementTime = 0;
    let isDocumentVisible = !document.hidden;
    
    // Check for visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // User left the page, calculate engagement time
        if (isDocumentVisible) {
          totalEngagementTime += (Date.now() - engagementStartTime);
          
          // Track the engagement time
          this.track('engagement_time', {
            engagement_time_seconds: Math.round(totalEngagementTime / 1000),
            page_path: window.location.pathname,
            page_title: document.title
          });
          
          // Send user_engagement event
          this.track('user_engagement', {
            engagement_time_seconds: Math.round(totalEngagementTime / 1000),
            page_path: window.location.pathname,
            page_title: document.title
          });
        }
        isDocumentVisible = false;
      } else {
        // User returned to the page, reset the timer
        engagementStartTime = Date.now();
        isDocumentVisible = true;
      }
    });
    
    // Track engagement time on page unload
    window.addEventListener('beforeunload', () => {
      if (isDocumentVisible) {
        totalEngagementTime += (Date.now() - engagementStartTime);
        
        this.track('engagement_time', {
          engagement_time_seconds: Math.round(totalEngagementTime / 1000),
          page_path: window.location.pathname,
          page_title: document.title,
          total_time_on_site_seconds: Math.round((Date.now() - this.startTime) / 1000)
        });
        
        // Send user_engagement event
        this.track('user_engagement', {
          engagement_time_seconds: Math.round(totalEngagementTime / 1000),
          page_path: window.location.pathname,
          page_title: document.title
        });
      }
    });
  }
  
  /**
   * Helper method to get scroll depth percentage
   */
  getScrollDepth() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    const scrollHeight = Math.max(
      document.body.scrollHeight,
      document.documentElement.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.offsetHeight
    ) - window.innerHeight;
    
    if (scrollHeight === 0) return 0;
    return Math.min(100, Math.round((scrollTop / scrollHeight) * 100));
  }
  
  /**
   * Helper method to get text content of an element
   */
  getElementText(element) {
    // First try innerText
    if (element.innerText && element.innerText.trim() !== '') {
      return element.innerText.trim().substring(0, 100);
    }
    
    // Then try textContent
    if (element.textContent && element.textContent.trim() !== '') {
      return element.textContent.trim().substring(0, 100);
    }
    
    // Then try value for input elements
    if (element.value && typeof element.value === 'string') {
      return element.value.trim().substring(0, 100);
    }
    
    // Then try alt text for images
    if (element.tagName === 'IMG' && element.alt) {
      return element.alt.trim().substring(0, 100);
    }
    
    // Finally check for aria-label
    if (element.getAttribute('aria-label')) {
      return element.getAttribute('aria-label').trim().substring(0, 100);
    }
    
    return '';
  }
  
  /**
   * Helper method to get domain from URL
   */
  getLinkDomain(url) {
    try {
      if (!url) return '';
      const urlObj = new URL(url);
      return urlObj.hostname;
    } catch (e) {
      return '';
    }
  }
  
  /**
   * Helper method to check if a link is outbound
   */
  isOutboundLink(url) {
    if (!url) return false;
    
    try {
      const urlObj = new URL(url);
      // Check if the link domain is different from current domain
      return urlObj.hostname !== window.location.hostname && 
             urlObj.protocol.startsWith('http'); // Only http/https links
    } catch (e) {
      return false;
    }
  }
  
  /**
   * Helper method to check if a form contains sensitive fields
   */
  isSecureForm(form) {
    for (let i = 0; i < form.elements.length; i++) {
      const element = form.elements[i];
      
      // Check for password fields
      if (element.type === 'password') return true;
      
      // Check for credit card fields
      const name = (element.name || '').toLowerCase();
      const id = (element.id || '').toLowerCase();
      if (name.includes('card') || name.includes('credit') || name.includes('ccnum') ||
          id.includes('card') || id.includes('credit') || id.includes('ccnum')) {
        return true;
      }
    }
    
    return false;
  }
  
  /**
   * Helper method to get all form field names
   */
  getFormFieldNames(form) {
    const fields = [];
    for (let i = 0; i < form.elements.length; i++) {
      const element = form.elements[i];
      if (element.name && !element.disabled) {
        fields.push(element.name);
      }
    }
    return fields.join(',');
  }
  
  /**
   * Helper method to get a unique identifier for an element
   */
  getElementIdentifier(element) {
    const tagName = element.tagName.toLowerCase();
    const id = element.id ? `#${element.id}` : '';
    const classList = Array.from(element.classList).map(c => `.${c}`).join('');
    const text = this.getElementText(element).substring(0, 20);
    
    // Add position-based identifier for better uniqueness
    const rect = element.getBoundingClientRect();
    const position = `@${Math.round(rect.left)},${Math.round(rect.top)}`;
    
    return `${tagName}${id}${classList}:${text}${position}`;
  }
  
  /**
   * Enable automatic tracking
   */
  enableAutoTracking() {
    this.autoTrackingEnabled = true;
    this.setupAutoTracking();
    this.tracker.logger.log("Automatic tracking enabled");
  }
  
  /**
   * Disable automatic tracking
   */
  disableAutoTracking() {
    this.autoTrackingEnabled = false;
    this.tracker.logger.log("Automatic tracking disabled");
  }
  
  /**
   * Process events that were tracked before initialization
   */
  processQueue() {
    if (Array.isArray(window.b2mq)) {
      this.tracker.logger.log("Processing queued B2M events:", window.b2mq.length);
      
      window.b2mq.forEach(args => {
        if (Array.isArray(args) && args.length > 0) {
          const method = args[0];
          const params = args.slice(1);
          
          if (typeof this[method] === 'function') {
            this[method].apply(this, params);
          }
        }
      });
      
      // Clear the queue
      window.b2mq = [];
    }
  }
  
  /**
   * Track a custom event
   * @param {string} eventName - Name of the event
   * @param {Object} eventData - Event data/properties
   */
  track(eventName, eventData = {}) {
    if (!this.isReady) {
      // Queue the event if not ready
      if (!window.b2mq) window.b2mq = [];
      window.b2mq.push(['track', eventName, eventData]);
      return;
    }
    
    // Consent check (GDPR/KVKK) – skip only non-system events
    // Consent-aware capture: when consent is pending we still CAPTURE the event
    // (it gets queued below); the send layer holds transmission until consent,
    // and giveConsent() flushes the queue. This keeps attribution from being
    // lost for users who accept consent after landing.
    if (!eventName.startsWith('$') && !this.hasConsent()) {
      this.tracker.logger.log(`Consent pending — capturing event for later: ${eventName}`);
    }
    
    // Check if event is enabled
    if (!this.tracker.config.isEventEnabled(eventName)) {
      this.tracker.logger.log(`Event '${eventName}' is disabled, skipping`);
      return;
    }
    
    // Event validation & sanitization
    eventData = this._sanitizeEventData(eventName, eventData);
    
    this.tracker.logger.log("B2M Event Tracked:", eventName, eventData);
    
    // Increment event counter for session metrics
    if (this._eventCount !== undefined) this._eventCount++;
    
    // Increment page count for page_view events
    if (eventName === 'page_view' && this._pageCount !== undefined) this._pageCount++;
    
    // Merge campaign/UTM params
    const campaignParams = this.tracker.storage.getItem('b2m_campaign_params') || {};

    // Merge referrer info
    const referrerInfo = this.tracker.storage.getItem('b2m_referrer') || {};

    // Merge first-touch attribution (set once on first visit, never overwritten)
    const initialUtm = this.tracker.storage.getItem('b2m_initial_utm') || {};
    const initialReferrer = this.tracker.storage.getItem('b2m_initial_referrer') || {};

    // Bot detection – don't block, tag the event so it can be filtered/counted in backend
    const botDetected = this.isBot();

    // Auto-collected device/browser properties (Mixpanel parity: os, browser, device_type…)
    const deviceProps = this.tracker.browserInfo.getAutoProperties();

    // Current page context — attached to every event so any event can be broken down
    // by page. page_view-specific events will override these via eventData if needed.
    const pageContext = {
      current_url:        window.location.href,
      current_path:       window.location.pathname,
      page_domain:        window.location.hostname,
      current_page_title: document.title || undefined
    };
    if (window.location.search) {
      pageContext.query_string = window.location.search;
    }

    // Create an event object with campaign data + bot flag
    const event = {
      event: eventName,
      ...deviceProps,       // os, browser, device_type, screen_width/height, language, timezone
      ...pageContext,       // current_url, current_path, current_page_title, query_string
      ...campaignParams,    // Last-touch UTM params
      ...referrerInfo,      // Current referrer
      ...initialUtm,        // First-touch UTM (initial_utm_source, etc.)
      ...initialReferrer,   // First-touch referrer (initial_referring_domain, etc.)
      traffic_source: this._deriveTrafficSource(campaignParams, referrerInfo),
      traffic_channel: this._deriveTrafficChannel(campaignParams, referrerInfo),
      ...eventData,         // Event-specific data (highest priority)
      interceptor: "b2m",
      event_group: this.tracker.config.getEventGroup(eventName),
      sdk_version: SDK_VERSION,
      event_seq: this._eventCount || 0,
      is_bot: botDetected   // Bot flag – backend can filter or count separately
    };
    
    // Add iframe context to every event when running in iframe mode
    if (this._isInIframe()) {
      if (!event.hasOwnProperty('is_iframe')) event.is_iframe = true;
      if (!event.hasOwnProperty('host_page_url')) event.host_page_url = this._getHostPageUrl();
      if (!event.hasOwnProperty('host_page_domain')) event.host_page_domain = this._getHostPageDomain();
    }
    
    // Add to the bulk queue
    this.tracker.storage.addToBulkQueue(event);
    
    // Check if this is a critical event that should be sent immediately
    const immediateEvents = this.tracker.config.immediateEvents || ['page_view', 'session_start', 'purchase'];
    if (immediateEvents.includes(eventName)) {
      this.tracker.logger.log(`Critical event '${eventName}' - sending immediately`);
      this.tracker.sendBulkEvents();
      return;
    }
    
    // Check if we should send immediately based on batch size
    if (this.tracker.storage.getBulkQueueSize() >= this.tracker.config.batchSize) {
      this.tracker.sendBulkEvents();
    }
  }
  
  /**
   * Track a page view event
   * @param {string} pagePath - Optional page path, defaults to current path
   * @param {Object} pageData - Additional page data
   */
  pageView(pagePath, pageData = {}) {
    const path = pagePath || window.location.pathname;
    
    const eventData = {
      page_path: path,
      page_title: document.title,
      page_location: window.location.href,
      page_referrer: document.referrer || "",
      ...pageData
    };
    
    this.track('page_view', eventData);
    this.pageViewed = true; // Mark page as viewed
    this.checkEngagementFlow(); // Check if engagement flow is complete
  }
  
  /**
   * Identify a user
   * @param {string} userId - User ID
   * @param {Object} userProps - User properties
   */
  identify(userId, userProps = {}) {
    if (userId) {
      this.tracker.storage.setItem('b2m_user_id', userId);
    }

    this.track('user_identify', {
      user_id: userId || this.tracker.userId.getId(),
      fingerprint_id: this.tracker.userId.getFingerprintId(),
      ...userProps
    });
  }
  
  // ============== USER PROFILE METHODS (Amplitude/Mixpanel parity) ==============
  
  /**
   * Set user profile properties – sent as a special identify event
   * @param {Object} properties - User profile properties (e.g. { name, email, plan })
   */
  setUserProperties(properties) {
    if (!properties || typeof properties !== 'object') return;
    
    let profile = this.tracker.storage.getItem('b2m_user_profile') || {};
    Object.assign(profile, properties);
    this.tracker.storage.setItem('b2m_user_profile', profile);
    
    // Fire an identify event so backend can persist the profile
    this.track('$identify', {
      $set: properties
    });
    
    this.tracker.logger.log('[B2M] User properties set:', properties);
  }
  
  /**
   * Set user profile properties only if they haven't been set yet
   * @param {Object} properties - Properties to set once (e.g. { first_login_date })
   */
  setUserPropertiesOnce(properties) {
    if (!properties || typeof properties !== 'object') return;
    
    let profile = this.tracker.storage.getItem('b2m_user_profile') || {};
    let newProps = {};
    
    for (const [key, value] of Object.entries(properties)) {
      if (profile[key] === undefined) {
        profile[key] = value;
        newProps[key] = value;
      }
    }
    
    if (Object.keys(newProps).length > 0) {
      this.tracker.storage.setItem('b2m_user_profile', profile);
      this.track('$identify', {
        $set_once: newProps
      });
    }
  }
  
  /**
   * Increment numeric user properties
   * @param {Object|string} properties - Property name (string) or { prop: amount } object
   * @param {number} [by=1] - Amount to increment (when first arg is string)
   */
  incrementUserProperty(properties, by = 1) {
    let profile = this.tracker.storage.getItem('b2m_user_profile') || {};
    let increments = {};
    
    if (typeof properties === 'string') {
      increments[properties] = by;
    } else if (typeof properties === 'object') {
      increments = properties;
    }
    
    for (const [key, amount] of Object.entries(increments)) {
      profile[key] = (profile[key] || 0) + (amount || 1);
    }
    
    this.tracker.storage.setItem('b2m_user_profile', profile);
    this.track('$identify', { $add: increments });
  }
  
  /**
   * Append values to list-type user properties
   * @param {Object} properties - { listProperty: valueToAppend }
   */
  appendUserProperty(properties) {
    if (!properties || typeof properties !== 'object') return;
    
    let profile = this.tracker.storage.getItem('b2m_user_profile') || {};
    for (const [key, value] of Object.entries(properties)) {
      if (!Array.isArray(profile[key])) profile[key] = [];
      profile[key].push(value);
    }
    
    this.tracker.storage.setItem('b2m_user_profile', profile);
    this.track('$identify', { $append: properties });
  }
  
  // ============== REVENUE TRACKING (Amplitude/Mixpanel parity) ==============
  
  /**
   * Track a revenue event with structured data
   * @param {Object} params - Revenue parameters
   * @param {number} params.price - Unit price
   * @param {number} [params.quantity=1] - Quantity
   * @param {string} [params.currency='TRY'] - ISO currency code
   * @param {string} [params.product_id] - Product identifier
   * @param {string} [params.order_id] - Order identifier
   * @param {string} [params.receipt] - Receipt for validation
   * @param {Object} [params.extra] - Additional properties
   */
  trackRevenue({ price, quantity = 1, currency = 'TRY', product_id, order_id, receipt, ...extra }) {
    if (typeof price !== 'number' || isNaN(price)) {
      this.tracker.logger.error('[B2M] trackRevenue requires a valid price');
      return;
    }
    
    const revenueData = {
      revenue: price * quantity,
      price,
      quantity,
      currency,
      ...(product_id && { product_id }),
      ...(order_id && { order_id }),
      ...(receipt && { receipt }),
      ...extra
    };
    
    this.track('purchase', revenueData);
    
    // Also increment lifetime revenue on user profile
    this.incrementUserProperty('lifetime_revenue', price * quantity);
    this.incrementUserProperty('total_purchases', 1);
  }
  
  // ============== CONSENT MANAGEMENT (GDPR/KVKK) ==============
  
  /**
   * Give tracking consent – must be called before events are sent
   * when config.requireConsent is true
   */
  giveConsent() {
    this.tracker.storage.setItem('b2m_consent', true);
    this.tracker.logger.log('[B2M] Tracking consent given');

    // Persist cookies that a CMP may have blocked before consent (re-resolve
    // cookieDomain + re-mirror attribution/identity) so cross-subdomain works.
    this.tracker.persistConsentedState();

    // Process any events queued before init…
    this.processQueue();
    // …and flush events captured while consent was pending (held at the send
    // layer). They now transmit with the resolved user_id + attribution.
    this.tracker.sendEvents();
  }

  /**
   * Revoke tracking consent – stops all future tracking
   */
  revokeConsent() {
    this.tracker.storage.setItem('b2m_consent', false);
    this.tracker.logger.log('[B2M] Tracking consent revoked');
    // Drop events captured while consent was pending — a user who declined
    // should not have that data sent if they later consent in a new session.
    this.tracker.storage.clearBulkQueue();
  }
  
  /**
   * Check if consent has been given
   * @returns {boolean}
   */
  hasConsent() {
    // If consent is not required, always return true
    if (!this.tracker?.config?.requireConsent) return true;
    return this.tracker.storage.getItem('b2m_consent') === true;
  }
  
  /**
   * Track add to cart event
   * @param {string} productId - ID of the product added to cart
   * @param {Object} productData - Additional product data
   */
  trackAddToCart(productId, productData = {}) {
    const eventData = {
      product_id: productId,
      ...productData,
      page_path: window.location.pathname,
      page_title: document.title
    };
    
    this.track('add_to_cart', eventData);
    this.itemAddedToCart = true; // Mark item as added to cart
    this.checkEngagementFlow(); // Check if engagement flow is complete
  }

  checkEngagementFlow() {
    if (this.sessionStarted && this.pageViewed && this.itemAddedToCart) {
      // Send user_engagement event
      this.track('user_engagement', {
        page_path: window.location.pathname,
        page_title: document.title
      });
      
      // Reset flags for future engagement tracking
      this.pageViewed = false;
      this.itemAddedToCart = false;
    }
  }
  
  // ============== EVENT GROUP CONTROL METHODS ==============
  
  /**
   * Enable an event group
   */
  enableEventGroup(groupName) {
    const result = this.tracker.config.enableEventGroup(groupName);
    if (result) {
      // Re-setup auto tracking for the newly enabled group
      const group = this.tracker.config.eventGroups[groupName];
      if (group.autoTrack) {
        this.setupGroupAutoTracking(groupName, group.autoTrack);
      }
      this.tracker.logger.log(`Event group '${groupName}' enabled`);
    } else {
      this.tracker.logger.warn(`Failed to enable event group '${groupName}'`);
    }
    return result;
  }
  
  /**
   * Disable an event group
   */
  disableEventGroup(groupName) {
    const result = this.tracker.config.disableEventGroup(groupName);
    if (result) {
      this.tracker.logger.log(`Event group '${groupName}' disabled`);
    } else {
      this.tracker.logger.warn(`Failed to disable event group '${groupName}' (core group cannot be disabled)`);
    }
    return result;
  }
  
  /**
   * Get enabled events
   */
  getEnabledEvents() {
    return this.tracker.config.getEnabledEvents();
  }
  
  // ============== NEW EVENT HANDLERS ==============
  
  /**
   * E-commerce: Product view tracking (view_item)
   */
  setupProductViewTracking() {
    // Auto-detect product pages and track views
    const productSelectors = [
      '[data-product-id]',
      '[itemtype="http://schema.org/Product"]',
      '[itemtype="https://schema.org/Product"]',
      '.product-detail',
      '.product-page',
      '.urun-detay',           // Ticimax
      '#product-container',
      '.productView'           // Shopify
    ];
    
    productSelectors.forEach(selector => {
      const element = document.querySelector(selector);
      if (element) {
        const productId = element.dataset.productId || 
                         element.querySelector('[itemprop="sku"]')?.content ||
                         element.querySelector('.product-code, .urun-kodu')?.textContent ||
                         'unknown';
        
        const productName = element.dataset.productName ||
                           element.querySelector('[itemprop="name"]')?.content ||
                           element.querySelector('.product-name, .product-title, .urun-adi')?.textContent ||
                           document.title;
        
        const priceElement = element.querySelector('[itemprop="price"]') ||
                            element.querySelector('.product-price, .price, .fiyat');
        const price = priceElement?.content || 
                     priceElement?.dataset.price ||
                     parseFloat(priceElement?.textContent.replace(/[^\d.,]/g, '').replace(',', '.')) ||
                     0;
        
        this.track('view_item', {
          item_id: productId,
          item_name: productName,
          price: price,
          currency: 'TRY',
          page_path: window.location.pathname,
          page_title: document.title,
          auto_detected: true
        });
      }
    });
  }
  
  /**
   * E-commerce: Product list view tracking (view_item_list)
   */
  setupProductListTracking() {
    // Detect product list pages (category, search results, etc.)
    const listSelectors = [
      '.product-list',
      '.products-grid',
      '.category-products',
      '.search-results',
      '.urun-listesi',         // Ticimax
      '[data-product-list]'
    ];
    
    listSelectors.forEach(selector => {
      const listElement = document.querySelector(selector);
      if (listElement) {
        const products = listElement.querySelectorAll('[data-product-id], .product-item, .urun-item');
        
        // Kategori bilgisi
        const categoryElement = document.querySelector('.category-name, .kategori-adi, h1.page-title');
        const categoryName = categoryElement?.textContent || 'Unknown Category';
        
        // Liste tipi
        const listType = window.location.pathname.includes('/search') ? 'search_results' :
                        window.location.pathname.includes('/category') ? 'category' :
                        'product_list';
        
        if (products.length > 0) {
          this.track('view_item_list', {
            item_list_name: categoryName,
            item_list_type: listType,
            items_count: products.length,
            page_path: window.location.pathname,
            auto_detected: true
          });
        }
      }
    });
  }
  
  /**
   * E-commerce: View cart tracking
   */
  setupViewCartTracking() {
    // Detect cart page
    const cartPageIndicators = [
      () => window.location.pathname.includes('/cart'),
      () => window.location.pathname.includes('/sepet'),
      () => window.location.pathname.includes('/basket'),
      () => document.querySelector('.cart-page, .sepet-sayfa, #cart-container')
    ];
    
    const isCartPage = cartPageIndicators.some(check => check());
    
    if (isCartPage) {
      // Wait a bit for cart to load
      setTimeout(() => {
        const cartItems = document.querySelectorAll('.cart-item, .sepet-urun, [data-cart-item]');
        
        if (cartItems.length > 0) {
          let totalValue = 0;
          const items = [];
          
          cartItems.forEach(item => {
            const priceElement = item.querySelector('.price, .fiyat, [data-price]');
            const quantityElement = item.querySelector('.quantity, .adet, [data-quantity]');
            
            const price = parseFloat(priceElement?.textContent.replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
            const quantity = parseInt(quantityElement?.value || quantityElement?.textContent) || 1;
            
            totalValue += price * quantity;
            
            items.push({
              item_id: item.dataset.productId || 'unknown',
              price: price,
              quantity: quantity
            });
          });
          
          this.track('view_cart', {
            items_count: cartItems.length,
            items: items,
            value: totalValue,
            currency: 'TRY',
            auto_detected: true
          });
        }
      }, 500);
    }
  }
  
  /**
   * E-commerce: Add to cart tracking
   */
  setupAddToCartTracking() {
    document.addEventListener('click', (event) => {
      const button = event.target.closest(
        '[data-action="add-to-cart"], ' +
        '.add-to-cart, .btn-add-cart, ' +
        '.sepeteEkle, .sepete-ekle, ' +      // Ticimax
        '[name="add_to_cart"], ' +            // WooCommerce
        '.product-form__submit'               // Shopify
      );
      
      if (button) {
        const productId = button.dataset.productId || 
                         button.dataset.product || 
                         button.closest('[data-product-id]')?.dataset.productId ||
                         'unknown';
        
        const productName = button.dataset.productName ||
                           button.closest('.product-item, .urun-item')?.querySelector('.product-name, .urun-adi')?.textContent ||
                           'Unknown Product';
        
        const priceElement = button.closest('.product-item, .urun-item, .product-detail')?.querySelector('.price, .fiyat');
        const price = parseFloat(priceElement?.textContent.replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
        
        const quantityInput = button.closest('form, .product-detail')?.querySelector('input[name="quantity"], .quantity');
        const quantity = parseInt(quantityInput?.value) || 1;
        
        this.track('add_to_cart', {
          item_id: productId,
          item_name: productName,
          price: price,
          quantity: quantity,
          value: price * quantity,
          currency: 'TRY',
          button_text: this.getElementText(button),
          page_path: window.location.pathname,
          auto_tracked: true
        });
      }
    });
  }
  
  /**
   * E-commerce: Remove from cart tracking
   */
  setupRemoveFromCartTracking() {
    document.addEventListener('click', (event) => {
      const button = event.target.closest(
        '[data-action="remove-from-cart"], ' +
        '.remove-from-cart, .btn-remove-cart, ' +
        '.sepettenCikar, .sepetten-cikar, ' +  // Ticimax
        '.remove, .product-remove'              // WooCommerce/Shopify
      );
      
      if (button) {
        const cartItem = button.closest('.cart-item, .sepet-urun, [data-cart-item]');
        const productId = button.dataset.productId || 
                         cartItem?.dataset.productId ||
                         cartItem?.dataset.itemId ||
                         'unknown';
        
        const productName = cartItem?.querySelector('.product-name, .item-name, .urun-adi')?.textContent || 'Unknown Product';
        
        const priceElement = cartItem?.querySelector('.price, .fiyat, .item-price');
        const price = parseFloat(priceElement?.textContent.replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
        
        const quantityElement = cartItem?.querySelector('.quantity, .miktar, [data-quantity]');
        const quantity = parseInt(quantityElement?.textContent || quantityElement?.value) || 1;
        
        this.track('remove_from_cart', {
          item_id: productId,
          item_name: productName,
          price: price,
          quantity: quantity,
          value: price * quantity,
          currency: 'TRY',
          page_path: window.location.pathname,
          auto_tracked: true
        });
      }
    });
  }
  
  /**
   * E-commerce: Checkout tracking
   */
  setupCheckoutTracking() {
    // Button click based checkout
    document.addEventListener('click', (event) => {
      const button = event.target.closest(
        '[data-action="checkout"], ' +
        '.checkout-btn, .btn-checkout, ' +
        '.odemeYap, .odeme-yap, ' +              // Ticimax
        '.wc-proceed-to-checkout, ' +            // WooCommerce
        '[name="checkout"]'                      // Shopify
      );
      
      if (button) {
        // Extract cart summary
        const cartItems = document.querySelectorAll('.cart-item, .sepet-urun, [data-cart-item]');
        const totalElement = document.querySelector('.cart-total, .sepet-toplam, .total-price');
        const totalValue = parseFloat(totalElement?.textContent.replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
        
        this.track('begin_checkout', {
          checkout_step: 1,
          item_count: cartItems.length,
          value: totalValue,
          currency: 'TRY',
          page_path: window.location.pathname,
          button_text: this.getElementText(button),
          auto_tracked: true
        });
      }
    });
    
    // URL-based checkout detection (for direct checkout page visits)
    if (window.location.pathname.match(/\/(checkout|odeme|siparis)/i)) {
      setTimeout(() => {
        const checkoutForm = document.querySelector('form[action*="checkout"], .checkout-form, .odeme-formu');
        if (checkoutForm && !checkoutForm.dataset.b2mTracked) {
          checkoutForm.dataset.b2mTracked = 'true';
          
          const totalElement = document.querySelector('.order-total, .siparis-toplam, .checkout-total');
          const totalValue = parseFloat(totalElement?.textContent.replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
          
          this.track('begin_checkout', {
            checkout_step: 1,
            value: totalValue,
            currency: 'TRY',
            page_path: window.location.pathname,
            trigger: 'page_load',
            auto_tracked: true
          });
        }
      }, 500);
    }
  }
  
  /**
   * E-commerce: Purchase tracking
   */
  setupPurchaseTracking() {
    // URL-based detection for thank you / order confirmation pages
    if (window.location.pathname.match(/\/(siparis-tamamlandi|tesekkurler|order-complete|thank-you|success)/i)) {
      setTimeout(() => {
        // Look for order details
        const orderIdElement = document.querySelector(
          '[data-order-id], .order-id, .siparis-no, #order_number'
        );
        const orderId = orderIdElement?.textContent.trim() || 
                       orderIdElement?.dataset.orderId || 
                       'ORDER-' + Date.now();
        
        // Get total value
        const totalElement = document.querySelector(
          '.order-total, .siparis-toplam, .total-price, .grand-total'
        );
        const totalValue = parseFloat(totalElement?.textContent.replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
        
        // Get items if available
        const items = [];
        document.querySelectorAll('.order-item, .siparis-urun, [data-order-item]').forEach(item => {
          const itemId = item.dataset.productId || item.dataset.itemId || '';
          const itemName = item.querySelector('.product-name, .urun-adi')?.textContent || '';
          const price = parseFloat(item.querySelector('.price, .fiyat')?.textContent.replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
          const quantity = parseInt(item.querySelector('.quantity, .miktar')?.textContent) || 1;
          
          if (itemId) {
            items.push({
              item_id: itemId,
              item_name: itemName,
              price: price,
              quantity: quantity
            });
          }
        });
        
        this.track('purchase', {
          transaction_id: orderId,
          value: totalValue,
          currency: 'TRY',
          items: items,
          item_count: items.length,
          page_path: window.location.pathname,
          trigger: 'page_load',
          auto_tracked: true
        });
      }, 500);
    }
    
    // Also look for order confirmation elements on any page
    const orderConfirmation = document.querySelector(
      '.order-confirmation, .purchase-complete, .siparis-onay'
    );
    
    if (orderConfirmation && !orderConfirmation.dataset.b2mTracked) {
      orderConfirmation.dataset.b2mTracked = 'true';
      
      const orderId = orderConfirmation.dataset.orderId || 
                     orderConfirmation.querySelector('[data-order-id]')?.dataset.orderId ||
                     'unknown';
      
      const totalElement = orderConfirmation.querySelector('.order-total, .siparis-toplam');
      const totalValue = parseFloat(totalElement?.textContent.replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
      
      this.track('purchase', {
        transaction_id: orderId,
        value: totalValue,
        currency: 'TRY',
        page_path: window.location.pathname,
        trigger: 'element_detection',
        auto_tracked: true
      });
    }
  }

  /**
   * E-commerce: Select item tracking (product click in lists)
   */
  setupSelectItemTracking() {
    document.addEventListener('click', (event) => {
      const productLink = event.target.closest(
        '.product-item a, .urun-item a, ' +
        '[data-product-id] a, .product-link'
      );
      
      if (productLink) {
        const productItem = productLink.closest('.product-item, .urun-item, [data-product-id]');
        const productId = productItem?.dataset.productId || 'unknown';
        const productName = productItem?.querySelector('.product-name, .urun-adi')?.textContent || 'Unknown';
        
        // Get list context
        const listContainer = productLink.closest('.product-list, .urun-listesi, .category-products');
        const listName = listContainer?.querySelector('.category-name, .kategori-adi')?.textContent || 
                        document.querySelector('h1')?.textContent || 
                        'Product List';
        
        this.track('select_item', {
          item_id: productId,
          item_name: productName,
          item_list_name: listName,
          link_url: productLink.href,
          page_path: window.location.pathname,
          auto_tracked: true
        });
      }
    });
  }

  /**
   * E-commerce: Shipping info tracking
   */
  setupShippingInfoTracking() {
    // Detect when shipping info is added/selected
    document.addEventListener('change', (event) => {
      const shippingInput = event.target.closest(
        '[name*="shipping"], [name*="kargo"], ' +
        '.shipping-method input, .kargo-secim input'
      );
      
      if (shippingInput && shippingInput.type === 'radio' && shippingInput.checked) {
        const shippingMethod = shippingInput.value || shippingInput.dataset.method || 'unknown';
        const shippingLabel = shippingInput.closest('label')?.textContent.trim() || 
                            shippingInput.nextElementSibling?.textContent.trim() || 
                            'Unknown Method';
        
        // Get shipping price if available
        const priceElement = shippingInput.closest('.shipping-option, .kargo-option')?.querySelector('.price, .ucret');
        const shippingPrice = parseFloat(priceElement?.textContent.replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
        
        this.track('add_shipping_info', {
          shipping_method: shippingMethod,
          shipping_label: shippingLabel,
          shipping_price: shippingPrice,
          currency: 'TRY',
          page_path: window.location.pathname,
          auto_tracked: true
        });
      }
    });
    
    // Also detect shipping form submission
    document.addEventListener('submit', (event) => {
      const shippingForm = event.target.closest(
        'form[action*="shipping"], form.shipping-form, form.kargo-form'
      );
      
      if (shippingForm) {
        const selectedShipping = shippingForm.querySelector('[name*="shipping"]:checked, [name*="kargo"]:checked');
        if (selectedShipping) {
          this.track('add_shipping_info', {
            shipping_method: selectedShipping.value,
            trigger: 'form_submit',
            page_path: window.location.pathname,
            auto_tracked: true
          });
        }
      }
    });
  }

  /**
   * E-commerce: Payment info tracking
   */
  setupPaymentInfoTracking() {
    // Detect when payment method is selected
    document.addEventListener('change', (event) => {
      const paymentInput = event.target.closest(
        '[name*="payment"], [name*="odeme"], ' +
        '.payment-method input, .odeme-yontemi input'
      );
      
      if (paymentInput && paymentInput.type === 'radio' && paymentInput.checked) {
        const paymentMethod = paymentInput.value || paymentInput.dataset.method || 'unknown';
        const paymentLabel = paymentInput.closest('label')?.textContent.trim() || 
                           paymentInput.nextElementSibling?.textContent.trim() || 
                           'Unknown Method';
        
        this.track('add_payment_info', {
          payment_method: paymentMethod,
          payment_label: paymentLabel,
          page_path: window.location.pathname,
          auto_tracked: true
        });
      }
    });
    
    // Detect credit card form interaction
    const ccInputs = document.querySelectorAll(
      'input[name*="card"], input[name*="kart"], ' +
      '[data-payment="credit_card"] input'
    );
    
    ccInputs.forEach(input => {
      input.addEventListener('focus', () => {
        if (!input.dataset.b2mTracked) {
          input.dataset.b2mTracked = 'true';
          this.track('add_payment_info', {
            payment_method: 'credit_card',
            trigger: 'form_interaction',
            page_path: window.location.pathname,
            auto_tracked: true
          });
        }
      }, { once: true });
    });
  }

  /**
   * E-commerce: Refund tracking
   */
  setupRefundTracking() {
    // Detect refund confirmation pages
    if (window.location.pathname.match(/\/(iade|refund|return)/i)) {
      setTimeout(() => {
        const refundConfirmation = document.querySelector(
          '.refund-confirmation, .iade-onay, [data-refund-id]'
        );
        
        if (refundConfirmation && !refundConfirmation.dataset.b2mTracked) {
          refundConfirmation.dataset.b2mTracked = 'true';
          
          const refundId = refundConfirmation.dataset.refundId || 
                          refundConfirmation.dataset.orderId ||
                          'REFUND-' + Date.now();
          
          const refundAmount = parseFloat(
            refundConfirmation.querySelector('.refund-amount, .iade-tutar')?.textContent
              .replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
          
          this.track('refund', {
            transaction_id: refundId,
            value: refundAmount,
            currency: 'TRY',
            page_path: window.location.pathname,
            auto_tracked: true
          });
        }
      }, 500);
    }
  }

  /**
   * E-commerce: Add to wishlist tracking
   */
  setupAddToWishlistTracking() {
    document.addEventListener('click', (event) => {
      const button = event.target.closest(
        '[data-action="add-to-wishlist"], ' +
        '.add-to-wishlist, .btn-wishlist, ' +
        '.favorilereEkle, .favorilere-ekle, ' +  // Ticimax
        '.add-to-favorites, .wishlist-add'
      );
      
      if (button) {
        const productId = button.dataset.productId || 
                         button.closest('[data-product-id]')?.dataset.productId ||
                         'unknown';
        
        const productName = button.dataset.productName ||
                           button.closest('.product-item, .urun-item, .product-detail')?.querySelector('.product-name, .urun-adi')?.textContent ||
                           'Unknown Product';
        
        const priceElement = button.closest('.product-item, .urun-item, .product-detail')?.querySelector('.price, .fiyat');
        const price = parseFloat(priceElement?.textContent.replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
        
        this.track('add_to_wishlist', {
          item_id: productId,
          item_name: productName,
          value: price,
          currency: 'TRY',
          page_path: window.location.pathname,
          auto_tracked: true
        });
      }
    });
  }

  /**
   * E-commerce: Remove from wishlist tracking
   */
  setupRemoveFromWishlistTracking() {
    document.addEventListener('click', (event) => {
      const button = event.target.closest(
        '[data-action="remove-from-wishlist"], ' +
        '.remove-from-wishlist, ' +
        '.favorilerdenCikar, .favorilerden-cikar, ' +  // Ticimax
        '.wishlist-remove'
      );
      
      if (button) {
        const wishlistItem = button.closest('.wishlist-item, .favori-urun, [data-wishlist-item]');
        const productId = button.dataset.productId || 
                         wishlistItem?.dataset.productId ||
                         'unknown';
        
        const productName = wishlistItem?.querySelector('.product-name, .urun-adi')?.textContent || 'Unknown Product';
        
        const priceElement = wishlistItem?.querySelector('.price, .fiyat');
        const price = parseFloat(priceElement?.textContent.replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
        
        this.track('remove_from_wishlist', {
          item_id: productId,
          item_name: productName,
          value: price,
          currency: 'TRY',
          page_path: window.location.pathname,
          auto_tracked: true
        });
      }
    });
  }

  /**
   * E-commerce: View promotion tracking
   */
  setupViewPromotionTracking() {
    // Detect promotional banners/sections on page load
    setTimeout(() => {
      const promotions = document.querySelectorAll(
        '.promotion-banner, .kampanya, .promo-section, ' +
        '[data-promotion-id], .discount-banner'
      );
      
      promotions.forEach((promo, index) => {
        if (!promo.dataset.b2mTracked) {
          promo.dataset.b2mTracked = 'true';
          
          const promoId = promo.dataset.promotionId || 
                         promo.dataset.kampanyaId ||
                         'PROMO-' + index;
          
          const promoName = promo.querySelector('h1, h2, h3, .title')?.textContent || 
                          promo.dataset.promotionName ||
                          'Unknown Promotion';
          
          this.track('view_promotion', {
            promotion_id: promoId,
            promotion_name: promoName,
            creative_name: promo.dataset.creative || 'banner',
            creative_slot: promo.dataset.slot || 'position-' + index,
            page_path: window.location.pathname,
            auto_tracked: true
          });
        }
      });
    }, 1000);
  }

  /**
   * E-commerce: Select promotion tracking (click on promotional elements)
   */
  setupSelectPromotionTracking() {
    document.addEventListener('click', (event) => {
      const promoLink = event.target.closest(
        '.promotion-banner a, .kampanya a, ' +
        '[data-promotion-id] a, .promo-link'
      );
      
      if (promoLink) {
        const promoContainer = promoLink.closest(
          '.promotion-banner, .kampanya, [data-promotion-id]'
        );
        
        const promoId = promoContainer?.dataset.promotionId || 
                       promoContainer?.dataset.kampanyaId ||
                       'unknown';
        
        const promoName = promoContainer?.querySelector('h1, h2, h3, .title')?.textContent || 
                        promoLink.textContent.trim() ||
                        'Unknown Promotion';
        
        this.track('select_promotion', {
          promotion_id: promoId,
          promotion_name: promoName,
          creative_name: promoContainer?.dataset.creative || 'banner',
          link_url: promoLink.href,
          page_path: window.location.pathname,
          auto_tracked: true
        });
      }
    });
  }

  /**
   * E-commerce: Coupon apply tracking
   */
  setupCouponApplyTracking() {
    // Detect coupon form submission
    document.addEventListener('submit', (event) => {
      const couponForm = event.target.closest(
        'form[action*="coupon"], form[action*="kupon"], ' +
        '.coupon-form, .kupon-form, ' +
        'form:has(input[name*="coupon"]), form:has(input[name*="kupon"])'
      );
      
      if (couponForm) {
        const couponInput = couponForm.querySelector(
          'input[name*="coupon"], input[name*="kupon"], ' +
          '.coupon-code, .kupon-kodu'
        );
        
        const couponCode = couponInput?.value || 'unknown';
        
        this.track('coupon_apply', {
          coupon_code: couponCode,
          trigger: 'form_submit',
          page_path: window.location.pathname,
          auto_tracked: true
        });
      }
    });
    
    // Detect coupon apply button click
    document.addEventListener('click', (event) => {
      const button = event.target.closest(
        '[data-action="apply-coupon"], ' +
        '.apply-coupon, .kupon-uygula, ' +
        '.btn-coupon-apply'
      );
      
      if (button) {
        const couponInput = button.closest('form, .coupon-section, .kupon-alani')?.querySelector(
          'input[name*="coupon"], input[name*="kupon"], .coupon-code'
        );
        
        const couponCode = couponInput?.value || 'unknown';
        
        this.track('coupon_apply', {
          coupon_code: couponCode,
          trigger: 'button_click',
          page_path: window.location.pathname,
          auto_tracked: true
        });
      }
    });
    
    // Detect successful coupon application (via DOM changes)
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            const successMessage = node.querySelector?.(
              '.coupon-success, .kupon-basarili, ' +
              '[class*="coupon"][class*="success"]'
            ) || (node.classList?.contains('coupon-success') ? node : null);
            
            if (successMessage && !successMessage.dataset.b2mTracked) {
              successMessage.dataset.b2mTracked = 'true';
              
              const couponCode = successMessage.dataset.couponCode || 
                               successMessage.querySelector('.code')?.textContent ||
                               'unknown';
              
              const discountElement = successMessage.querySelector('.discount, .indirim');
              const discountAmount = parseFloat(discountElement?.textContent.replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
              
              this.track('coupon_apply', {
                coupon_code: couponCode,
                discount_amount: discountAmount,
                currency: 'TRY',
                trigger: 'success_confirmation',
                page_path: window.location.pathname,
                auto_tracked: true
              });
            }
          }
        });
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Media: Video tracking
   */
  setupVideoTracking() {
    document.addEventListener('play', (event) => {
      if (event.target.tagName === 'VIDEO') {
        const video = event.target;
        this.track('video_start', {
          video_id: video.id || video.src,
          video_title: video.title || video.dataset.title || '',
          video_duration: video.duration || 0,
          auto_tracked: true
        });
      }
    }, true);
    
    document.addEventListener('ended', (event) => {
      if (event.target.tagName === 'VIDEO') {
        const video = event.target;
        this.track('video_complete', {
          video_id: video.id || video.src,
          video_duration: video.duration || 0,
          watch_time: video.currentTime || 0,
          auto_tracked: true
        });
      }
    }, true);
    
    document.addEventListener('pause', (event) => {
      if (event.target.tagName === 'VIDEO') {
        const video = event.target;
        this.track('video_pause', {
          video_id: video.id || video.src,
          pause_time: video.currentTime || 0,
          auto_tracked: true
        });
      }
    }, true);
  }
  
  /**
   * Media: Image zoom tracking
   */
  setupImageZoomTracking() {
    document.addEventListener('click', (event) => {
      const image = event.target.closest('.zoomable, [data-zoom], .zoom-trigger');
      if (image) {
        this.track('image_zoom', {
          image_id: image.id || image.src,
          image_alt: image.alt || '',
          auto_tracked: true
        });
      }
    });
  }
  
  /**
   * Search: Search tracking
   */
  setupSearchTracking() {
    // Track search from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const searchTerms = ['q', 'query', 'search', 's', 'term', 'keyword'];
    
    searchTerms.forEach(param => {
      const searchTerm = urlParams.get(param);
      if (searchTerm) {
        this.track('search', {
          search_term: searchTerm,
          search_parameter: param,
          results_count: this.getSearchResultsCount(),
          auto_tracked: true
        });
      }
    });
    
    // Track search form submissions
    document.addEventListener('submit', (event) => {
      const form = event.target;
      const searchInput = form.querySelector('input[name*="search"], input[name*="query"], input[name*="q"]');
      if (searchInput && searchInput.value) {
        this.track('search', {
          search_term: searchInput.value,
          search_source: 'form_submit',
          auto_tracked: true
        });
      }
    });
  }
  
  /**
   * Search: Filter tracking
   */
  setupFilterTracking() {
    document.addEventListener('change', (event) => {
      const filter = event.target.closest('.filter, [data-filter], .facet');
      if (filter) {
        this.track('filter_apply', {
          filter_type: filter.name || filter.dataset.filter || 'unknown',
          filter_value: filter.value || filter.textContent,
          auto_tracked: true
        });
      }
    });
  }
  
  /**
   * Search: Sort tracking
   */
  setupSortTracking() {
    document.addEventListener('change', (event) => {
      const sortElement = event.target.closest('.sort, [data-sort], .sort-by');
      if (sortElement) {
        this.track('sort_change', {
          sort_type: sortElement.value || sortElement.textContent,
          sort_direction: sortElement.dataset.direction || 'asc',
          auto_tracked: true
        });
      }
    });
  }
  
  /**
   * Communication: Phone click tracking
   */
  setupPhoneClickTracking() {
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href^="tel:"]');
      if (link) {
        this.track('phone_click', {
          phone_number: link.href.replace('tel:', ''),
          link_text: this.getElementText(link),
          auto_tracked: true
        });
      }
    });
  }
  
  /**
   * Communication: Contact form tracking
   */
  setupContactFormTracking() {
    document.addEventListener('submit', (event) => {
      const form = event.target;
      const isContactForm = form.classList.contains('contact-form') || 
                           form.querySelector('input[name*="email"]') || 
                           form.querySelector('textarea[name*="message"]');
      
      if (isContactForm) {
        this.track('contact_form', {
          form_id: form.id || 'unknown',
          form_fields: this.getFormFieldNames(form),
          auto_tracked: true
        });
      }
    });
  }
  
  /**
   * Marketing: Banner click tracking
   */
  setupBannerClickTracking() {
    document.addEventListener('click', (event) => {
      const banner = event.target.closest('.banner, [data-banner], .hero-banner, .promo-banner');
      if (banner) {
        this.track('banner_click', {
          banner_id: banner.id || banner.dataset.banner || 'unknown',
          banner_position: banner.dataset.position || 'unknown',
          banner_text: this.getElementText(banner),
          auto_tracked: true
        });
      }
    });
  }
  
  /**
   * Marketing: Newsletter signup tracking
   */
  setupNewsletterTracking() {
    document.addEventListener('submit', (event) => {
      const form = event.target;
      const isNewsletterForm = form.classList.contains('newsletter') || 
                              form.querySelector('input[name*="newsletter"]') ||
                              form.querySelector('input[name*="subscribe"]');
      
      if (isNewsletterForm) {
        this.track('newsletter_signup', {
          form_id: form.id || 'unknown',
          signup_location: form.dataset.location || 'unknown',
          auto_tracked: true
        });
      }
    });
  }
  
  /**
   * Marketing: Popup tracking
   */
  setupPopupTracking() {
    // Track when modals/popups become visible
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const element = mutation.target;
          if (element.classList.contains('show') || element.classList.contains('active')) {
            const isPopup = element.classList.contains('modal') || 
                           element.classList.contains('popup') ||
                           element.classList.contains('overlay');
            
            if (isPopup) {
              this.track('popup_view', {
                popup_id: element.id || 'unknown',
                popup_type: element.dataset.type || 'unknown',
                auto_tracked: true
              });
            }
          }
        }
      });
    });
    
    observer.observe(document.body, {
      attributes: true,
      subtree: true,
      attributeFilter: ['class']
    });
  }
  
  /**
   * Performance: API timing tracking
   */
  setupApiTimingTracking() {
    // Monkey patch fetch to track API calls
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const startTime = performance.now();
      try {
        const response = await originalFetch(...args);
        const endTime = performance.now();
        
        this.track('api_timing', {
          api_url: args[0],
          response_time_ms: Math.round(endTime - startTime),
          status_code: response.status,
          auto_tracked: true
        });
        
        return response;
      } catch (error) {
        const endTime = performance.now();
        this.track('api_timing', {
          api_url: args[0],
          response_time_ms: Math.round(endTime - startTime),
          error: error.message,
          auto_tracked: true
        });
        throw error;
      }
    };
  }
  
  /**
   * Performance: Network change tracking
   */
  setupNetworkTracking() {
    window.addEventListener('online', () => {
      this.track('network_change', {
        network_status: 'online',
        auto_tracked: true
      });
    });
    
    window.addEventListener('offline', () => {
      this.track('network_change', {
        network_status: 'offline',
        auto_tracked: true
      });
    });
    
    // Track connection type if available
    if ('connection' in navigator) {
      const connection = navigator.connection;
      this.track('network_change', {
        connection_type: connection.effectiveType,
        downlink: connection.downlink,
        auto_tracked: true
      });
    }
  }
  
  /**
   * Engagement: User idle tracking
   */
  setupIdleTracking() {
    if (this._idleTrackingSetup) return;
    this._idleTrackingSetup = true;

    let idleTimer;
    let isIdle = false;
    const IDLE_TIME = 5 * 60 * 1000; // 5 minutes
    
    const resetIdleTimer = () => {
      clearTimeout(idleTimer);
      if (isIdle) {
        isIdle = false;
        this.track('user_active', {
          auto_tracked: true
        });
      }
      
      idleTimer = setTimeout(() => {
        isIdle = true;
        this.track('user_idle', {
          idle_time_ms: IDLE_TIME,
          auto_tracked: true
        });
      }, IDLE_TIME);
    };
    
    ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'].forEach(event => {
      document.addEventListener(event, resetIdleTimer, true);
    });
    
    resetIdleTimer();
  }
  
  /**
   * Engagement: Tab change tracking
   */
  setupTabChangeTracking() {
    document.addEventListener('click', (event) => {
      const tab = event.target.closest('[role="tab"], .tab, .nav-tab');
      if (tab) {
        this.track('tab_change', {
          tab_id: tab.id || tab.dataset.tab || 'unknown',
          tab_text: this.getElementText(tab),
          auto_tracked: true
        });
      }
    });
  }
  
  /**
   * Engagement: Modal tracking
   */
  setupModalTracking() {
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-toggle="modal"], [data-bs-toggle="modal"]');
      if (trigger) {
        const modalId = trigger.dataset.target || trigger.dataset.bsTarget || 'unknown';
        this.track('modal_open', {
          modal_id: modalId.replace('#', ''),
          trigger_element: this.getElementText(trigger),
          auto_tracked: true
        });
      }
    });
  }
  
  /**
   * Mobile: Orientation change tracking
   */
  setupOrientationTracking() {
    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        this.track('orientation_change', {
          orientation: screen.orientation ? screen.orientation.angle : window.orientation,
          screen_width: screen.width,
          screen_height: screen.height,
          auto_tracked: true
        });
      }, 100);
    });
  }
  
  /**
   * Mobile: App download tracking
   */
  setupAppDownloadTracking() {
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href*="play.google.com"], a[href*="apps.apple.com"], a[href*="app-store"]');
      if (link) {
        let store = 'unknown';
        if (link.href.includes('play.google.com')) store = 'google_play';
        else if (link.href.includes('apps.apple.com')) store = 'app_store';
        
        this.track('app_download_click', {
          app_store: store,
          app_url: link.href,
          auto_tracked: true
        });
      }
    });
  }
  
  // ============== HELPER METHODS ==============
  
  /**
   * Get search results count from page
   */
  getSearchResultsCount() {
    const countSelectors = [
      '.results-count',
      '.search-results-count',
      '[data-results-count]'
    ];
    
    for (const selector of countSelectors) {
      const element = document.querySelector(selector);
      if (element) {
        const count = parseInt(element.textContent.match(/\d+/)?.[0] || '0');
        return count;
      }
    }
    
    // Fallback: count result items
    const resultItems = document.querySelectorAll('.result-item, .search-result, .product-item');
    return resultItems.length;
  }
  
  /**
   * Setup GTM DataLayer listener to intercept events
   */
  setupGTMDataLayerListener() {
    // Check if GTM listener is enabled in config
    if (!this.tracker.config.enableGTMDataLayerListener) {
      this.tracker.logger.log('[B2M] GTM DataLayer listener disabled in config');
      return;
    }

    if (typeof window === 'undefined') return;

    // Initialize dataLayer if it doesn't exist
    window.dataLayer = window.dataLayer || [];

    // Get excluded events from config
    const excludedEvents = this.tracker.config.gtmExcludeEvents || ['page_view'];

    const applyOverride = () => {
      // Don't re-apply if our override is already in place
      if (window.dataLayer.push.__b2m_listener) return;

      const originalPush = window.dataLayer.push;

      const b2mPush = (...args) => {
        // Call original push first
        originalPush.apply(window.dataLayer, args);

        // Process each pushed item
        args.forEach(item => {
          if (item && typeof item === 'object' && item.event) {
            const { event, ...params } = item;

            if (!event.startsWith('gtm.') && !excludedEvents.includes(event)) {
              this.track(event, {
                ...params,
                source: 'gtm_datalayer',
                page_url: window.location.href,
                page_path: window.location.pathname,
              });

              this.tracker.logger.log(`[B2M] GTM DataLayer event intercepted: ${event}`, params);
            } else if (excludedEvents.includes(event)) {
              this.tracker.logger.log(`[B2M] GTM DataLayer event excluded: ${event}`);
            }
          }
        });

        return window.dataLayer.length;
      };

      b2mPush.__b2m_listener = true;
      window.dataLayer.push = b2mPush;

      this.tracker.logger.log('[B2M] GTM DataLayer listener applied');
    };

    // Apply immediately
    applyOverride();

    // Re-apply every 500ms for the first 5s to survive GTM re-initialization
    let attempts = 0;
    const guardInterval = setInterval(() => {
      if (!window.dataLayer.push.__b2m_listener) {
        this.tracker.logger.log('[B2M] GTM DataLayer push was replaced, re-applying listener...');
        applyOverride();
      }
      if (++attempts >= 10) clearInterval(guardInterval);
    }, 500);

    this.tracker.logger.log('[B2M] GTM DataLayer listener initialized');
    this.tracker.logger.log('[B2M] Excluded events:', excludedEvents);
  }

  /**
   * Setup SPA (Single Page Application) navigation tracking
   * Automatically tracks route changes in frameworks like React, Vue, Framer, etc.
   */
  setupSPANavigationTracking() {
    // Check if SPA tracking is enabled in config
    if (!this.tracker.config.enableSPATracking) {
      this.tracker.logger.log('SPA navigation tracking disabled in config');
      return;
    }
    
    if (!this.tracker.config.isEventEnabled('page_view')) {
      this.tracker.logger.log('SPA navigation tracking disabled (page_view not enabled)');
      return;
    }
    
    this.tracker.logger.log('Setting up SPA navigation tracking');

    let currentPath = window.location.pathname;
    let currentSearch = window.location.search;
    let lastNavigationTime = Date.now();
    // Track the last URL for which a page_view was actually sent so that
    // simultaneous triggers (pushState timeout + polling interval) cannot
    // fire a second event for the same destination URL.
    let lastTrackedUrl = window.location.pathname + window.location.search;

    // SPA navigation timeout (shorter than general duplicate prevention)
    const spaTimeout = this.tracker.config.spaNavigationTimeout || 300;

    // Track route changes by monitoring pathname and search params only (NOT title)
    const checkRouteChange = () => {
      const newPath = window.location.pathname;
      const newSearch = window.location.search;
      const newUrl = newPath + newSearch;
      const now = Date.now();

      // Only track if URL actually changed (path or query string)
      const urlChanged = newPath !== currentPath || newSearch !== currentSearch;

      // Skip if this exact URL was already sent (guards against simultaneous triggers)
      const alreadySentForUrl = newUrl === lastTrackedUrl;

      // Only track if URL changed AND enough time has passed AND not already sent
      if (urlChanged && (now - lastNavigationTime) >= spaTimeout && !alreadySentForUrl) {
        const previousPath = currentPath;

        currentPath = newPath;
        currentSearch = newSearch;
        lastNavigationTime = now;
        lastTrackedUrl = newUrl;
        
        this.tracker.logger.log(`SPA navigation detected: ${previousPath} → ${currentPath}`);
        
        // Reset scroll depth for new page
        this.lastScrollDepth = 0;
        
        // Small delay to let the page title update before tracking
        setTimeout(() => {
          this.pageView(currentPath, {
            previous_path: previousPath,
            navigation_type: 'spa_navigation',
            query_string: currentSearch
          });
        }, 100); // Wait 100ms for title to update
      }
    };
    
    // Poll for route changes with configurable interval (default 100ms for faster detection)
    const pollingInterval = this.tracker.config.spaPollingInterval || 100;
    setInterval(checkRouteChange, pollingInterval);
    
    // Intercept History API methods (pushState, replaceState)
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;
    
    history.pushState = (...args) => {
      originalPushState.apply(history, args);
      // Small delay to allow title update
      setTimeout(checkRouteChange, 50);
    };
    
    history.replaceState = (...args) => {
      originalReplaceState.apply(history, args);
      setTimeout(checkRouteChange, 50);
    };
    
    // Listen to popstate (back/forward buttons)
    window.addEventListener('popstate', () => {
      setTimeout(checkRouteChange, 50);
    });
    
    // Listen to hashchange for hash-based routing
    window.addEventListener('hashchange', () => {
      setTimeout(checkRouteChange, 50);
    });
    
    this.tracker.logger.log(`SPA navigation tracking enabled (polling: ${pollingInterval}ms, timeout: ${spaTimeout}ms)`);
  }
} 